// @ts-nocheck
/*
 * CTISignal — exact port of the ct-admin-dashboard Agent CTI.
 *
 * Route: /cti-signal
 *
 * Pure mock replica: no API calls, no callStore, no softphone bootstrap,
 * no BreakContext. All state is local to a self-contained useSoftphone
 * hook driven by the mock data in this file. Wire the endpoints yourself
 * later — the callsites are marked with // WIRE tags.
 *
 * Portals to document.body so the AppShell chrome doesn't show through.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftRight, BadgeCheck, CalendarClock, Check, ChevronLeft, ChevronRight,
  CircleDot, Clock, Coffee, Delete, Grid3x3, History, LayoutDashboard,
  Lightbulb, LogOut, Mail, MapPin, MessageSquareQuote, Mic, MicOff, Monitor,
  Moon, NotebookPen, PanelLeftClose, PanelLeftOpen, Pause, Phone, PhoneCall,
  PhoneIncoming, PhoneOff, PhoneOutgoing, Play, Search, Signal, Sparkles, Star,
  Sun, Target, User, UserPlus, UserSquare2, Users, X,
} from 'lucide-react';
import { useThemeStore } from '@/lib/theme/store';
import { getUser } from '@/utilities/setting';
import { loginAsProfile, logoutAsProfile } from '@/features/cti/softphone/loginAsProfile';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { CalendarTimeInput } from '@/components/ui/calendar-inputs';

/* ================================================================== */
/*  Mock data — every value is verbatim from ct-admin-dashboard          */
/* ================================================================== */

const ME = {
  name: 'Aarti Deshmukh',
  initials: 'AD',
  extension: '4021',
  tl: 'Sana Qureshi',
  team: 'Team A',
  department: 'Consumer Sales',
  process: 'Prepaid Retention',
  campaigns: ['Retention — Prepaid', 'Winback — Churned'],
  queues: ['Q-RETENTION', 'Q-WINBACK'],
  shift: '09:00 – 18:00',
  loginAt: '08:56',
  calls: 142,
  connectPct: 84,
  converted: 22,
  target: 130,
  ahtSec: 268,
  acwSec: 96,
  occupancy: 81,
  breakUsedMin: 34,
  breakAllowMin: 60,
};

/* Backend profile types — must match what loginAsProfile / logoutAsProfile
 * post to /users/login-as. Casing is significant on the server side. */
const DEVICES = [
  { key: 'WEBRTC', label: 'WebRTC', hint: 'Browser softphone' },
  { key: 'Soft Phone', label: 'Softphone', hint: 'Desktop client' },
  { key: 'Mobile', label: 'Mobile', hint: 'Ring your registered mobile number' },
];

const ACTIVE_CUSTOMER = {
  name: 'Rajesh Iyer',
  number: '+91 98204 77315',
  altNumber: '+91 22 4067 1180',
  email: 'r.iyer@fastmail.in',
  city: 'Mumbai',
  language: 'Marathi / English',
  customerId: 'CU-4471902',
  plan: 'Prepaid ₹399 · 84 days',
  tenure: '6 yr 4 mo',
  arpu: '₹412 / mo',
  segment: 'High value',
  riskScore: 72,
  lastRecharge: '18 Aug 2026',
  balance: '₹41.20',
  tags: ['Churn risk', 'Repeat caller', 'No DND'],
  consent: 'Recording consent on file',
};

const CALL_META = {
  direction: 'Inbound',
  queue: 'Q-RETENTION',
  campaign: 'Retention — Prepaid',
  waitedSec: 34,
  routedBy: 'Skill · Marathi',
  callId: 'CL-2026-0826-88412',
  trunk: 'TRK-02',
  codec: 'opus / 48 kHz',
  mos: 4.4,
  recording: 'Recording',
  priority: 'High',
  attempt: 1,
};

const CALL_HISTORY = [
  { at: '14 Aug 2026 · 16:42', dir: 'Outbound', agent: 'Aarti Deshmukh', dur: '6:18', dispo: 'Callback', tone: 'call', note: 'Asked to be called after 20 Aug. Comparing our ₹399 pack against a competitor offer.' },
  { at: '02 Aug 2026 · 11:07', dir: 'Inbound', agent: 'Joseph Mathew', dur: '4:02', dispo: 'Not Interested', tone: 'crit', note: 'Unhappy about data throttling after 1.5 GB/day. Escalated to network team, ticket NT-88214.' },
  { at: '27 Jul 2026 · 19:20', dir: 'Inbound', agent: 'IVR only', dur: '1:12', dispo: 'Abandoned', tone: 'off', note: 'Dropped in queue after 68s wait.' },
  { at: '11 Jul 2026 · 10:35', dir: 'Outbound', agent: 'Bhavana Reddy', dur: '8:44', dispo: 'Interested', tone: 'good', note: 'Open to an annual pack if OTT bundle is included. Wanted pricing over email.' },
  { at: '03 Jun 2026 · 15:58', dir: 'Inbound', agent: 'Aarti Deshmukh', dur: '3:26', dispo: 'Converted', tone: 'good', note: 'Recharged ₹399 on call. Confirmed Marathi as preferred language.' },
];

const SAVED_NOTES = [
  { at: '14 Aug · 16:48', by: 'Aarti Deshmukh', text: 'Prefers evening calls after 19:00. Do not offer SMS packs.' },
  { at: '02 Aug · 11:14', by: 'Joseph Mathew', text: 'Network complaint open — check NT-88214 before pitching.' },
  { at: '03 Jun · 16:02', by: 'Aarti Deshmukh', text: 'Speaks Marathi. Wife handles the second number on the account.' },
];

const SCRIPT = {
  name: 'Prepaid Retention — churn-risk inbound',
  version: 'v4.2',
  steps: [
    { kind: 'say', title: 'Opening', body: 'Namaskar, Rajesh ji. Aarti here from Nexus Voice. Thank you for calling — I can see you have been with us for over six years. How may I help you today?', hint: 'Use Marathi greeting. Confirm you are speaking to the account holder.' },
    { kind: 'check', title: 'Verify identity', body: 'Confirm two of: registered name, city, last recharge amount.', hint: 'Do not proceed with account changes until two match.', checks: ['Name confirmed', 'City confirmed', 'Last recharge confirmed'] },
    { kind: 'ask', title: 'Understand the reason', body: 'May I know what prompted the call today? Is it about the data speed issue we logged earlier?', hint: 'Ticket NT-88214 is open. Acknowledge it before any pitch.', branches: [
      { label: 'Network complaint', next: 'Go to Objection: throttling' },
      { label: 'Wants to port out', next: 'Go to Retention offer' },
      { label: 'Pricing query', next: 'Go to Retention offer' },
    ] },
    { kind: 'say', title: 'Acknowledge the open ticket', body: 'I can see ticket NT-88214 is still open on the data speed. I am adding a priority note now so the network team revisits it within 24 hours.', hint: 'Never promise a resolution date beyond 24h.' },
    { kind: 'say', title: 'Retention offer', body: 'Because you have been with us six years, I can move you to the annual ₹3,499 pack — that is ₹291 a month, unlimited calls, 2 GB a day, and the OTT bundle you asked about in July.', hint: 'Anchor on the monthly equivalent, not the annual figure.' },
    { kind: 'ask', title: 'Close', body: 'Shall I apply it to this number right now? The change takes effect from your next cycle.', hint: 'If hesitant, offer the callback slot after 19:00 — his stated preference.', branches: [
      { label: 'Accepts', next: 'Disposition: Converted' },
      { label: 'Needs time', next: 'Disposition: Callback' },
      { label: 'Declines', next: 'Go to Objection: competitor' },
    ] },
  ],
  objections: [
    { q: 'Competitor is cheaper', a: 'Compare total value, not headline price: our annual pack includes the OTT bundle (₹499/yr standalone) and no daily-limit throttling above 2 GB.' },
    { q: 'Data gets throttled', a: 'Acknowledge the open ticket. The annual pack raises the daily limit from 1.5 GB to 2 GB, which is the threshold his usage crosses.' },
    { q: 'Wants to think about it', a: 'Secure a firm callback slot after 19:00 and note the exact offer discussed so the next agent does not restart the conversation.' },
  ],
};

const DISPOSITIONS_LIST = [
  { label: 'Converted', tone: 'good', needsCallback: false },
  { label: 'Interested', tone: 'good', needsCallback: false },
  { label: 'Callback', tone: 'call', needsCallback: true },
  { label: 'Follow-up', tone: 'warn', needsCallback: true },
  { label: 'Not Interested', tone: 'crit', needsCallback: false },
  { label: 'Wrong Number', tone: 'off', needsCallback: false },
  { label: 'No Response', tone: 'off', needsCallback: false },
];

const CONTACTS = {
  internal: [
    { name: 'Sana Qureshi', role: 'Team Leader', ext: '2201', state: 'available' },
    { name: 'Priya Raghavan', role: 'Team Leader · Team A', ext: '2204', state: 'onCall' },
    { name: 'Network L2 Desk', role: 'Escalation', ext: '5100', state: 'available' },
    { name: 'Retention Specialist', role: 'Skill queue', ext: '5220', state: 'available' },
    { name: 'Billing Support', role: 'Skill queue', ext: '5310', state: 'onCall' },
    { name: 'Joseph Mathew', role: 'Agent · Team C', ext: '4088', state: 'acw' },
    { name: 'Bhavana Reddy', role: 'Agent · Team A', ext: '4033', state: 'available' },
    { name: 'Supervisor Hotline', role: 'Barge / whisper', ext: '2000', state: 'available' },
  ],
  queues: [
    { name: 'Q-RETENTION', waiting: 3, agents: 42 },
    { name: 'Q-BILLING', waiting: 0, agents: 18 },
    { name: 'Q-NETWORK-L2', waiting: 1, agents: 9 },
    { name: 'Q-ONBOARD', waiting: 14, agents: 18 },
  ],
  external: [
    { name: 'Rajesh Iyer', number: '+91 98204 77315', city: 'Mumbai', tag: 'On call' },
    { name: 'Sunita Iyer', number: '+91 22 4067 1180', city: 'Mumbai', tag: 'Same account' },
    { name: 'M. Fernandes', number: '+91 98111 77420', city: 'Pune', tag: 'Callback due' },
    { name: 'T. Ahluwalia', number: '+91 90045 63118', city: 'Nagpur', tag: 'Callback due' },
    { name: 'S. Deshpande', number: '+91 98204 41127', city: 'Mumbai', tag: 'Overdue' },
    { name: 'A. Bhatt', number: '+91 97654 02311', city: 'Thane', tag: 'Lead' },
  ],
};

const RECENT_CALLS = [
  { at: '18:31', name: 'Rajesh Iyer', number: '+91 98204 77315', dir: 'in', dur: '—', dispo: 'Live', tone: 'call' },
  { at: '18:12', name: 'K. Bhosale', number: '+91 99671 20884', dir: 'out', dur: '4:41', dispo: 'Interested', tone: 'good' },
  { at: '17:58', name: 'N. Pillai', number: '+91 98330 55127', dir: 'out', dur: '1:18', dispo: 'No Response', tone: 'off' },
  { at: '17:44', name: 'D. Kamat', number: '+91 90290 71140', dir: 'in', dur: '7:02', dispo: 'Converted', tone: 'good' },
  { at: '17:21', name: 'S. Rane', number: '+91 98676 33019', dir: 'out', dur: '3:34', dispo: 'Callback', tone: 'call' },
  { at: '17:04', name: 'V. Naik', number: '+91 99201 84462', dir: 'out', dur: '2:11', dispo: 'Not Interested', tone: 'crit' },
  { at: '16:47', name: 'P. Gokhale', number: '+91 98195 60023', dir: 'in', dur: '5:29', dispo: 'Follow-up', tone: 'warn' },
];

const MY_FOLLOWUPS = [
  { at: 'Today 19:00', name: 'Rajesh Iyer', number: '+91 98204 77315', reason: 'Annual pack decision', tone: 'call' },
  { at: 'Today 19:30', name: 'S. Rane', number: '+91 98676 33019', reason: 'Sent pricing on email', tone: 'call' },
  { at: 'Tomorrow 10:00', name: 'P. Gokhale', number: '+91 98195 60023', reason: 'Spouse to decide', tone: 'warn' },
  { at: 'Tomorrow 15:00', name: 'A. Bhatt', number: '+91 97654 02311', reason: 'Port-out intent', tone: 'warn' },
  { at: 'Overdue 2h 14m', name: 'M. Fernandes', number: '+91 98111 77420', reason: 'Second attempt', tone: 'crit' },
];

const MY_LEADS = [
  { name: 'K. Bhosale', number: '+91 99671 20884', source: 'Winback list', score: 84, stage: 'Interested' },
  { name: 'R. Salvi', number: '+91 98505 21173', source: 'Web enquiry', score: 78, stage: 'New' },
  { name: 'H. Mirza', number: '+91 90040 11928', source: 'Winback list', score: 71, stage: 'New' },
  { name: 'G. Kulkarni', number: '+91 98220 66471', source: 'Referral', score: 66, stage: 'Attempted' },
  { name: 'V. Naik', number: '+91 99201 84462', source: 'Winback list', score: 41, stage: 'Closed' },
];

// hourly volume — pre-computed so no Date.now/Math.random needed
const MY_HOURLY = [
  { label: '08:00', incoming: 5, outgoing: 4, inConnected: 5, outConnected: 3 },
  { label: '09:00', incoming: 8, outgoing: 5, inConnected: 7, outConnected: 4 },
  { label: '10:00', incoming: 9, outgoing: 6, inConnected: 8, outConnected: 5 },
  { label: '11:00', incoming: 9, outgoing: 7, inConnected: 8, outConnected: 5 },
  { label: '12:00', incoming: 6, outgoing: 8, inConnected: 5, outConnected: 6 },
  { label: '13:00', incoming: 6, outgoing: 6, inConnected: 5, outConnected: 5 },
  { label: '14:00', incoming: 9, outgoing: 8, inConnected: 8, outConnected: 6 },
  { label: '15:00', incoming: 9, outgoing: 8, inConnected: 8, outConnected: 6 },
  { label: '16:00', incoming: 7, outgoing: 6, inConnected: 6, outConnected: 5 },
  { label: '17:00', incoming: 6, outgoing: 4, inConnected: 5, outConnected: 3 },
  { label: '18:00', incoming: 4, outgoing: 2, inConnected: 4, outConnected: 2 },
];

const MY_DISPOSITIONS = [
  { label: 'Converted', tone: 'good', count: 22 },
  { label: 'Interested', tone: 'good', count: 26 },
  { label: 'Callback', tone: 'call', count: 19 },
  { label: 'Follow-up', tone: 'warn', count: 11 },
  { label: 'Not Interested', tone: 'crit', count: 24 },
  { label: 'No Response', tone: 'off', count: 12 },
  { label: 'Wrong Number', tone: 'off', count: 5 },
];

/* ================================================================== */
/*  Helpers                                                            */
/* ================================================================== */

const nf = (n) => Number(n).toLocaleString('en-IN');
const mmssLong = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;

const TONE = {
  good: { text: 't-good', bg: 'bg-good', soft: 's-good' },
  call: { text: 't-call', bg: 'bg-call', soft: 's-call' },
  warn: { text: 't-warn', bg: 'bg-warn', soft: 's-warn' },
  crit: { text: 't-crit', bg: 'bg-crit', soft: 's-crit' },
  off: { text: 't-off', bg: 'bg-off', soft: 's-off' },
};

/* ================================================================== */
/*  useSoftphone — state machine from softphone.js, verbatim behavior  */
/* ================================================================== */
const IDLE = {
  status: 'idle', sec: 0, muted: false, held: false,
  parties: [], consulting: null, customer: null, meta: null, dialed: '',
};

function useSoftphone() {
  // start on a live inbound call so the demo lands on the interesting screen
  const [call, setCall] = useState(() => ({
    ...IDLE,
    status: 'connected',
    sec: 214,
    customer: ACTIVE_CUSTOMER,
    meta: CALL_META,
  }));
  const [device, setDevice] = useState('webrtc');
  const [dialpad, setDialpad] = useState('');
  const [dtmf, setDtmf] = useState('');
  const [log, setLog] = useState([
    { at: '18:31:02', text: 'Inbound call routed from Q-RETENTION (skill: Marathi)' },
    { at: '18:31:36', text: 'Answered by Aarti Deshmukh · recording started' },
  ]);

  // wall clock for the event log: fixed base plus seconds elapsed
  const elapsed = useRef(0);
  const stamp = useCallback(() => {
    const base = Date.parse('2026-08-26T18:40:00');
    return new Date(base + elapsed.current * 1000).toLocaleTimeString('en-GB');
  }, []);
  const note = useCallback(
    (text) => setLog((l) => [...l.slice(-8), { at: stamp(), text }]),
    [stamp],
  );

  useEffect(() => {
    const id = setInterval(() => {
      if (document.hidden) return;
      elapsed.current += 1;
      setCall((c) => (c.status === 'idle' ? c : { ...c, sec: c.sec + 1 }));
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const answer = useCallback(() => {
    setCall((c) => ({ ...c, status: 'connected', sec: 0 }));
    note('Call answered · recording started');
  }, [note]);

  const dial = useCallback((number) => {
    const n = number || dialpad;
    if (!n) return;
    setCall({
      ...IDLE,
      status: 'dialing',
      customer: { ...ACTIVE_CUSTOMER, name: 'Dialling…', number: n },
      meta: { ...CALL_META, direction: 'Outbound', queue: '—', waitedSec: 0 },
    });
    setDialpad('');
    note('Dialling ' + n);
    setTimeout(() => {
      setCall((c) => (c.status === 'dialing'
        ? { ...c, status: 'connected', sec: 0, customer: { ...c.customer, name: 'Outbound party' } }
        : c
      ));
    }, 1800);
  }, [dialpad, note]);

  const hangup = useCallback(() => {
    setCall((c) => ({ ...IDLE, status: 'wrapup', sec: 0, customer: c.customer, meta: c.meta }));
    note('Call ended · after-call work started');
  }, [note]);

  const endWrap = useCallback(() => {
    setCall({ ...IDLE });
    note('Disposition saved · agent available');
  }, [note]);

  const toggleMute = useCallback(() => {
    setCall((c) => ({ ...c, muted: !c.muted }));
  }, []);
  const toggleHold = useCallback(() => {
    setCall((c) => ({ ...c, held: !c.held }));
  }, []);

  const firstMute = useRef(true);
  useEffect(() => {
    if (firstMute.current) { firstMute.current = false; return; }
    if (call.status !== 'connected') return;
    note(call.muted ? 'Microphone muted' : 'Microphone live');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call.muted]);

  const firstHold = useRef(true);
  useEffect(() => {
    if (firstHold.current) { firstHold.current = false; return; }
    if (call.status !== 'connected') return;
    note(call.held ? 'Customer placed on hold' : 'Customer taken off hold');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call.held]);

  const addConference = useCallback((party) => {
    setCall((c) => {
      if (c.parties.some((p) => p.name === party.name)) return c;
      return { ...c, parties: [...c.parties, party], held: false };
    });
  }, []);
  const dropConference = useCallback((name) => {
    setCall((c) => ({ ...c, parties: c.parties.filter((p) => p.name !== name) }));
  }, []);

  const prevParties = useRef([]);
  useEffect(() => {
    const before = prevParties.current.map((p) => p.name);
    const now = call.parties.map((p) => p.name);
    now.filter((n) => !before.includes(n)).forEach((n) => note('Conference: ' + n + ' joined'));
    before.filter((n) => !now.includes(n)).forEach((n) => note('Conference: ' + n + ' left'));
    prevParties.current = call.parties;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call.parties]);

  const startConsult = useCallback((party) => {
    setCall((c) => ({ ...c, held: true, consulting: party }));
    note('Consulting ' + party.name + ' · customer on hold');
  }, [note]);

  const cancelConsult = useCallback(() => {
    setCall((c) => ({ ...c, held: false, consulting: null }));
    note('Consult ended · back with customer');
  }, [note]);

  const completeTransfer = useCallback((party, kind) => {
    note((kind === 'blind' ? 'Blind' : 'Warm') + ' transfer to ' + party.name + ' completed');
    setCall((c) => ({
      ...IDLE,
      status: kind === 'blind' ? 'idle' : 'wrapup',
      customer: c.customer, meta: c.meta,
    }));
  }, [note]);

  const sendDtmf = useCallback((k) => {
    setDtmf((d) => (d + k).slice(-12));
    note('DTMF ' + k + ' sent');
  }, [note]);

  const simulateInbound = useCallback(() => {
    setCall({ ...IDLE, status: 'ringing', customer: ACTIVE_CUSTOMER, meta: CALL_META });
    note('Inbound call from ' + ACTIVE_CUSTOMER.number + ' · screen-pop opened');
  }, [note]);

  const label = ({
    idle: 'Ready',
    dialing: 'Dialling',
    ringing: 'Incoming',
    connected: call.held ? 'On hold' : 'On call',
    wrapup: 'After-call work',
  })[call.status] || call.status;
  const tone = ({
    idle: 'good',
    dialing: 'call',
    ringing: 'call',
    connected: call.held ? 'warn' : 'call',
    wrapup: 'warn',
  })[call.status] || 'off';

  return {
    call, label, tone, device, setDevice, dialpad, setDialpad, dtmf, log,
    answer, dial, hangup, endWrap, toggleMute, toggleHold,
    addConference, dropConference, startConsult, cancelConsult, completeTransfer,
    sendDtmf, simulateInbound,
  };
}

/* ================================================================== */
/*  Page                                                               */
/* ================================================================== */

export default function CTISignalPage() {
  const navigate = useNavigate();
  const phone = useSoftphone();
  const [dialerOpen, setDialerOpen] = useState(true);
  const [ctx, setCtx] = useState('history');
  const [mode, setMode] = useState('auto');
  const themeMode = useThemeStore((s) => s.mode);
  const setThemeMode = useThemeStore((s) => s.setMode);

  const { call } = phone;
  const onCall = call.status === 'connected' || call.status === 'wrapup' || call.status === 'ringing';
  const showLive = mode === 'auto' ? onCall : mode === 'live';

  // scroll-lock so the AppShell body doesn't scroll behind the portal
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  /* ---------- Real telephony login / logout wiring ------------- */

  // Lazy-load the softphone module once. Its initDailer() runs on import
  // and self-registers SIP handlers against `keypadConnect2`/`disconnect2`,
  // so loginAsProfile('WEBRTC') can drive the same DOM buttons v1 used.
  useEffect(() => {
    let cancelled = false;
    import('@/features/cti/softphone/demo-1').catch((err) => {
      if (!cancelled) console.error('[cti-signal] Softphone bootstrap failed', err);
    });
    return () => { cancelled = true; };
  }, []);

  const [userMeta] = useState(() => {
    const u = getUser() || {};
    return { userId: u?.id || null };
  });

  const configuredProfiles = useMemo(() => {
    const list = getUser()?.agentData?.agentTelephony?.profile || [];
    // Return a Set of backend `type` values the agent actually has configured.
    return new Set(list.map((p) => p?.type).filter(Boolean));
  }, []);

  const [activeProfileType, setActiveProfileType] = useState(() => {
    try { return sessionStorage.getItem('activeLoginType'); } catch { return null; }
  });
  const [pendingProfileType, setPendingProfileType] = useState(null);

  // Keep the active-type badge in sync when loginAsProfile / logoutAsProfile
  // fire their custom 'sessionStorageChange' event (or if another tab flips it).
  useEffect(() => {
    const sync = () => {
      try { setActiveProfileType(sessionStorage.getItem('activeLoginType')); } catch {}
    };
    window.addEventListener('sessionStorageChange', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('sessionStorageChange', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const handlePickProfile = useCallback(async (type) => {
    if (!userMeta.userId) return toast.error('No user id — reload and try again');
    if (pendingProfileType) return;
    if (!configuredProfiles.has(type)) {
      return toast.error(`${type} is not configured on your account`);
    }

    setPendingProfileType(type);
    try {
      // Same profile is active → treat click as logout.
      if (activeProfileType === type) {
        await logoutAsProfile(userMeta.userId, type);
        return;
      }
      // Switching profiles: log the current one out first so both server
      // and softphone module reach a clean state before the new register.
      if (activeProfileType && activeProfileType !== type) {
        try { await logoutAsProfile(userMeta.userId, activeProfileType); }
        catch (err) { console.warn('[cti-signal] logout of prior profile failed', err); }
      }
      await loginAsProfile(userMeta.userId, type);
    } catch (err) {
      /* helpers already toasted the specific message */
      console.error('[cti-signal] profile switch failed', err);
    } finally {
      setPendingProfileType(null);
    }
  }, [userMeta.userId, activeProfileType, pendingProfileType, configuredProfiles]);

  const shell = (
    <div className="cti-v3">
      <StyleTag />

      <Dialer
        phone={phone}
        open={dialerOpen}
        setOpen={setDialerOpen}
        activeProfileType={activeProfileType}
        pendingProfileType={pendingProfileType}
        configuredProfiles={configuredProfiles}
        onPickProfile={handlePickProfile}
      />

      <main className="cti-v3-main">
        <div className="cti-v3-topbar">
          <span className="cti-v3-agent-avatar">{ME.initials}</span>
          <div className="cti-v3-agent-body">
            <div className="cti-v3-agent-name">
              {ME.name}
              <span className="num cti-v3-agent-ext">x{ME.extension}</span>
            </div>
            <div className="cti-v3-agent-team">{ME.team} · reports to {ME.tl}</div>
          </div>
          <div style={{ flex: 1 }} />
          <Tabs
            size="sm"
            value={showLive ? 'live' : 'dash'}
            onChange={(k) => setMode(k === 'live' ? 'live' : 'dash')}
            tabs={[
              { key: 'live', label: 'Live call' },
              { key: 'dash', label: 'My dashboard' },
            ]}
          />
          <ThemeToggle mode={themeMode} setMode={setThemeMode} />
          <button className="cti-v3-btn" onClick={() => navigate('/dashboard')}>
            <LogOut size={12} /> Exit
          </button>
        </div>

        <div className="cti-v3-work">
          {showLive ? (
            <div className="cti-v3-live-grid">
              <div className="cti-v3-center">
                <CustomerPanel customer={call.customer} meta={call.meta} sec={call.sec} status={call.status} />
                {call.status === 'ringing' ? (
                  <Panel className="cti-v3-ring-empty">
                    <div className="cti-v3-ring-title">Incoming call — answer to continue</div>
                    <p className="cti-v3-ring-body">
                      The screen-pop above already carries the caller's identity and history, so the agent reads context before speaking.
                    </p>
                  </Panel>
                ) : (
                  <>
                    <ScriptPanel />
                    <DispositionPanel status={call.status} onSave={phone.endWrap} />
                  </>
                )}
              </div>

              <div className="cti-v3-right">
                <div className="cti-v3-right-tabs">
                  <Tabs
                    size="sm"
                    value={ctx}
                    onChange={setCtx}
                    tabs={[
                      { key: 'history', label: 'History' },
                      { key: 'contacts', label: 'Contacts' },
                      { key: 'details', label: 'Call details' },
                    ]}
                  />
                </div>
                {ctx === 'history' && <HistoryPanel />}
                {ctx === 'contacts' && <ContactsPanel phone={phone} />}
                {ctx === 'details' && <CallDetailsPanel meta={call.meta} sec={call.sec} call={call} />}

                <Panel>
                  <div className="cti-v3-panel-head">
                    <PhoneCall size={12} className="cti-v3-muted-ico" />
                    <span className="cti-v3-panel-title">Call events</span>
                  </div>
                  <div className="cti-v3-events">
                    {phone.log.slice().reverse().map((l, i) => (
                      <div key={i} className="cti-v3-event-row">
                        <span className="num cti-v3-event-at">{l.at}</span>
                        <span className="cti-v3-event-text">{l.text}</span>
                      </div>
                    ))}
                  </div>
                </Panel>
              </div>
            </div>
          ) : (
            <AgentDashboard />
          )}
        </div>
      </main>
    </div>
  );

  return createPortal(shell, document.body);
}

/* ================================================================== */
/*  AgentDashboard — idle mode                                          */
/* ================================================================== */

function AgentDashboard() {
  const maxD = Math.max(...MY_DISPOSITIONS.map((d) => d.count));

  const stats = [
    { label: 'Calls handled', value: ME.calls, sub: 'target ' + ME.target, tone: 'good' },
    { label: 'Connect %', value: ME.connectPct + '%', sub: 'team median 74%', tone: 'good' },
    { label: 'Converted', value: ME.converted, sub: 'best on the team', tone: 'good' },
    { label: 'Avg handle time', value: '4:28', sub: 'ACW 1:36', tone: null },
    { label: 'Occupancy', value: ME.occupancy + '%', sub: 'centre 74%', tone: 'good' },
    { label: 'Break used', value: ME.breakUsedMin + 'm', sub: 'of ' + ME.breakAllowMin + 'm', tone: 'warn' },
  ];

  return (
    <div className="cti-v3-dash">
      {/* KPI slides */}
      <section>
        <SectionHead
          eyebrow="My performance"
          title="Today so far"
          sub={'Signed in ' + ME.loginAt + ' · shift ' + ME.shift}
          right={
            <Pill tone="good">
              <Target size={10} />
              {Math.round((ME.calls / ME.target) * 100)}% of target
            </Pill>
          }
        />
        <div className="cti-v3-kpi-grid">
          {stats.map((s) => (
            <Panel key={s.label} className="cti-v3-kpi">
              <div className="cti-v3-kpi-label">{s.label}</div>
              <div className={`cti-v3-kpi-value num ${s.tone ? TONE[s.tone].text : ''}`}>
                {s.value}
              </div>
              <div className="cti-v3-kpi-sub">{s.sub}</div>
            </Panel>
          ))}
        </div>
      </section>

      {/* volume trend + dispositions */}
      <section className="cti-v3-dash-grid-1">
        <Panel className="cti-v3-p-4">
          <SectionHead
            eyebrow="Call volume"
            title="Incoming vs outgoing"
            sub="Solid bars are connected calls · 142 total today"
          />
          <HourlyBars data={MY_HOURLY} />
        </Panel>

        <Panel className="cti-v3-p-4">
          <SectionHead
            eyebrow="My call outcomes"
            title="Disposition mix"
            sub="119 connected calls dispositioned"
          />
          <div className="cti-v3-dispo-list">
            {MY_DISPOSITIONS.map((d) => (
              <div key={d.label}>
                <div className="cti-v3-dispo-row-head">
                  <span className="cti-v3-dispo-label">
                    <span className={`cti-v3-dispo-swatch ${TONE[d.tone].bg}`} />
                    {d.label}
                  </span>
                  <span className="num cti-v3-dispo-count">{d.count}</span>
                </div>
                <div className="cti-v3-dispo-track">
                  <div
                    className={`cti-v3-dispo-fill ${TONE[d.tone].bg}`}
                    style={{ width: (d.count / maxD) * 100 + '%' }}
                  />
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </section>

      {/* follow-ups + leads */}
      <section className="cti-v3-dash-grid-2">
        <Panel className="cti-v3-p-4">
          <SectionHead
            eyebrow="Follow-ups"
            title="My callbacks"
            sub="Oldest first · one is already overdue"
            right={<button className="cti-v3-link">Open full page →</button>}
          />
          <div className="cti-v3-table-wrap">
            <table className="cti-v3-table">
              <thead><tr><Th>When</Th><Th>Customer</Th><Th>Reason</Th></tr></thead>
              <tbody>
                {MY_FOLLOWUPS.map((f) => (
                  <tr key={f.number}>
                    <Td>
                      <span className={`num cti-v3-fu-at ${TONE[f.tone].text}`}>{f.at}</span>
                    </Td>
                    <Td>
                      <span className="cti-v3-fu-name">{f.name}</span>
                      <span className="num cti-v3-fu-num">{f.number}</span>
                    </Td>
                    <Td className="cti-v3-fu-reason">{f.reason}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>

        <Panel className="cti-v3-p-4">
          <SectionHead
            eyebrow="Assigned leads"
            title="My pipeline"
            sub="Scored highest first"
            right={<button className="cti-v3-link">Open full page →</button>}
          />
          <div className="cti-v3-table-wrap">
            <table className="cti-v3-table">
              <thead><tr><Th>Lead</Th><Th>Source</Th><Th>Score</Th><Th>Stage</Th></tr></thead>
              <tbody>
                {MY_LEADS.map((l) => (
                  <tr key={l.number}>
                    <Td>
                      <span className="cti-v3-fu-name">{l.name}</span>
                      <span className="num cti-v3-fu-num">{l.number}</span>
                    </Td>
                    <Td className="cti-v3-fu-reason">{l.source}</Td>
                    <Td>
                      <MiniBar
                        value={l.score} max={100}
                        tone={l.score >= 75 ? 'good' : l.score >= 60 ? 'warn' : 'off'}
                        width={38} showValue={String(l.score)}
                      />
                    </Td>
                    <Td className="cti-v3-fu-stage">{l.stage}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </section>

      {/* roster info */}
      <section>
        <Panel className="cti-v3-roster">
          <div>
            <div className="cti-v3-roster-label">Department</div>
            <div className="cti-v3-roster-value">{ME.department}</div>
          </div>
          <div>
            <div className="cti-v3-roster-label">Process</div>
            <div className="cti-v3-roster-value">{ME.process}</div>
          </div>
          <div>
            <div className="cti-v3-roster-label">Campaigns</div>
            <div className="cti-v3-roster-campaigns">
              {ME.campaigns.map((c) => <Pill key={c} tone="call">{c}</Pill>)}
            </div>
          </div>
          <div>
            <div className="cti-v3-roster-label">Queues</div>
            <div className="cti-v3-roster-value num">{ME.queues.join(' · ')}</div>
          </div>
          <div className="cti-v3-roster-break">
            <Coffee size={13} className="cti-v3-muted-ico" />
            <span>Break {ME.breakUsedMin}/{ME.breakAllowMin} min used</span>
          </div>
        </Panel>
      </section>
    </div>
  );
}

/* Inline SVG bar chart — replaces Recharts (no dep needed) */
function HourlyBars({ data }) {
  const w = 640;
  const h = 210;
  const pad = { top: 8, right: 8, bottom: 26, left: 32 };
  const cw = w - pad.left - pad.right;
  const ch = h - pad.top - pad.bottom;
  const max = Math.max(...data.map((d) => d.incoming + d.outgoing));
  const bw = (cw / data.length) * 0.36;
  const gap = (cw / data.length) * 0.14;
  const step = cw / data.length;

  return (
    <div className="cti-v3-chart-wrap">
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="cti-v3-chart">
        {/* grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((f) => {
          const y = pad.top + ch - ch * f;
          return (
            <g key={f}>
              <line x1={pad.left} x2={pad.left + cw} y1={y} y2={y} className="cti-v3-grid-line" />
              <text x={pad.left - 4} y={y + 3} textAnchor="end" className="cti-v3-axis">
                {Math.round(max * f)}
              </text>
            </g>
          );
        })}
        {data.map((d, i) => {
          const x0 = pad.left + i * step + step / 2 - bw - gap / 2;
          const x1 = pad.left + i * step + step / 2 + gap / 2;
          const inH = (d.incoming / max) * ch;
          const inCH = (d.inConnected / max) * ch;
          const outH = (d.outgoing / max) * ch;
          const outCH = (d.outConnected / max) * ch;
          return (
            <g key={d.label}>
              {/* incoming bar (light) */}
              <rect x={x0} y={pad.top + ch - inH} width={bw} height={inH} rx="2" className="cti-v3-bar-in-soft" />
              {/* incoming connected (solid) */}
              <rect x={x0} y={pad.top + ch - inCH} width={bw} height={inCH} rx="2" className="cti-v3-bar-in" />
              {/* outgoing bar (light) */}
              <rect x={x1} y={pad.top + ch - outH} width={bw} height={outH} rx="2" className="cti-v3-bar-out-soft" />
              {/* outgoing connected (solid) */}
              <rect x={x1} y={pad.top + ch - outCH} width={bw} height={outCH} rx="2" className="cti-v3-bar-out" />
              <text x={pad.left + i * step + step / 2} y={h - 8} textAnchor="middle" className="cti-v3-axis">
                {d.label}
              </text>
            </g>
          );
        })}
      </svg>
      <div className="cti-v3-legend">
        <span><span className="cti-v3-legend-swatch bg-call" /> Incoming</span>
        <span><span className="cti-v3-legend-swatch bg-call soft" /> Incoming (softer = offered)</span>
        <span><span className="cti-v3-legend-swatch bg-good" /> Outgoing connected</span>
        <span><span className="cti-v3-legend-swatch bg-good soft" /> Outgoing offered</span>
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Dialer                                                             */
/* ================================================================== */

function Ctl({ icon: Icon, label, active, danger, disabled, onClick, compact }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={`cti-v3-ctl ${compact ? 'compact' : ''} ${danger ? 'danger' : ''} ${active ? 'active' : ''}`}
    >
      <Icon size={compact ? 15 : 14} />
      {!compact && <span>{label}</span>}
    </button>
  );
}

function Dialer({
  phone, open, setOpen,
  activeProfileType, pendingProfileType, configuredProfiles, onPickProfile,
}) {
  const [picker, setPicker] = useState(null);
  const [padOpen, setPadOpen] = useState(false);
  const { call } = phone;
  const live = call.status === 'connected';
  const active = call.status !== 'idle';
  const canControl = live;
  const dirIcon = call.meta?.direction === 'Outbound' ? PhoneOutgoing : PhoneIncoming;

  const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'];

  if (!open) {
    return (
      <>
        <aside className="cti-v3-dialer collapsed">
          <button
            className="cti-v3-collapse-btn"
            onClick={() => setOpen(true)}
            title="Expand dialer"
          >
            <PanelLeftOpen size={16} />
          </button>

          <div className="cti-v3-hairline" />

          {/* state + timer, always first */}
          <button
            className={`cti-v3-mini-state ${active ? 'active' : ''}`}
            onClick={() => setOpen(true)}
            title={phone.label}
          >
            <LiveDot tone={phone.tone} />
            <span className="num cti-v3-mini-timer">
              {active ? mmssLong(call.sec) : '--:--'}
            </span>
          </button>

          {call.status === 'ringing' && (
            <Ctl compact icon={Phone} label="Answer" onClick={phone.answer} active />
          )}
          <Ctl compact icon={call.muted ? MicOff : Mic} label="Mute" active={call.muted} disabled={!canControl} onClick={phone.toggleMute} />
          <Ctl compact icon={call.held ? Play : Pause} label="Hold" active={call.held} disabled={!canControl} onClick={phone.toggleHold} />
          <Ctl compact icon={ArrowLeftRight} label="Transfer" disabled={!canControl} onClick={() => setPicker('transfer')} />
          <Ctl compact icon={UserPlus} label="Conference" disabled={!canControl} onClick={() => setPicker('conference')} />
          <Ctl compact icon={Grid3x3} label="Keypad" disabled={!canControl} onClick={() => { setOpen(true); setPadOpen(true); }} />

          <div className="cti-v3-dialer-bottom">
            {call.parties.length > 0 && (
              <span className="num cti-v3-conf-count">+{call.parties.length}</span>
            )}
            {active ? (
              <Ctl
                compact icon={PhoneOff}
                label={call.status === 'wrapup' ? 'End wrap' : 'Hang up'}
                danger
                onClick={call.status === 'wrapup' ? phone.endWrap : phone.hangup}
              />
            ) : (
              <Ctl compact icon={PhoneCall} label="Dial" onClick={() => { setOpen(true); setPadOpen(true); }} />
            )}
          </div>
        </aside>
        {picker && <ContactPicker mode={picker} phone={phone} onClose={() => setPicker(null)} />}
      </>
    );
  }

  return (
    <>
      <aside className="cti-v3-dialer expanded">
        {/* header */}
        <div className="cti-v3-dialer-head">
          <span className="cti-v3-dialer-mark"><PhoneCall size={13} /></span>
          <span className="cti-v3-dialer-title">Dialer</span>
          <button className="cti-v3-icon-btn" onClick={() => setOpen(false)} title="Collapse dialer">
            <PanelLeftClose size={15} />
          </button>
        </div>

        {/* device switcher — real telephony login/logout */}
        <div className="cti-v3-section">
          <div className="cti-v3-device-caption">
            <span className="cti-v3-caption">Device</span>
            {activeProfileType ? (
              <span className="cti-v3-device-status">
                <span className="cti-v3-conn-dot ok" />
                {activeProfileType} connected
              </span>
            ) : (
              <span className="cti-v3-device-status muted">
                <span className="cti-v3-conn-dot off" />
                Not connected
              </span>
            )}
          </div>
          <div className="cti-v3-device-tabs">
            {DEVICES.map((d) => {
              const active = activeProfileType === d.key;
              const configured = configuredProfiles?.has?.(d.key);
              const busy = pendingProfileType === d.key;
              const disabled = !configured || Boolean(pendingProfileType);
              return (
                <button
                  key={d.key}
                  onClick={() => onPickProfile?.(d.key)}
                  disabled={disabled}
                  title={
                    !configured
                      ? `${d.label} is not configured on your account`
                      : active
                        ? `Click to log out of ${d.label}`
                        : d.hint
                  }
                  className={`cti-v3-device-tab ${active ? 'active' : ''} ${!configured ? 'unavailable' : ''}`}
                >
                  {busy ? <Loader2 size={11} className="cti-spin" /> : d.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* live call card */}
        <div className="cti-v3-section">
          <div className="cti-v3-live-head">
            <Pill tone={phone.tone}><LiveDot tone={phone.tone} />{phone.label}</Pill>
            <span className="num cti-v3-live-timer">
              {active ? mmssLong(call.sec) : '00:00'}
            </span>
          </div>

          {active ? (
            <div className="cti-v3-live-body">
              <div className="cti-v3-live-caller">
                {(() => { const I = dirIcon; return <I size={12} className="cti-v3-muted-ico" />; })()}
                <span className="cti-v3-caller-name">{call.customer?.name}</span>
              </div>
              <div className="num cti-v3-caller-num">{call.customer?.number}</div>
              {call.meta && (
                <div className="cti-v3-meta-row">
                  <span className="cti-v3-meta-chip">{call.meta.queue}</span>
                  <span className="cti-v3-meta-chip">waited {call.meta.waitedSec}s</span>
                  {call.muted && <Pill tone="warn">Muted</Pill>}
                  {call.held && <Pill tone="warn">On hold</Pill>}
                </div>
              )}

              {call.consulting && (
                <div className="cti-v3-consult">
                  <div className="cti-v3-consult-title">
                    Consulting {call.consulting.name}
                  </div>
                  <div className="cti-v3-consult-actions">
                    <button
                      onClick={() => phone.completeTransfer(call.consulting, 'warm')}
                      className="cti-v3-consult-btn primary"
                    >
                      Complete transfer
                    </button>
                    <button onClick={phone.cancelConsult} className="cti-v3-consult-btn">Back</button>
                  </div>
                </div>
              )}

              {call.parties.length > 0 && (
                <div className="cti-v3-conf-box">
                  <div className="cti-v3-caption">Conference · {call.parties.length + 2} parties</div>
                  <div className="cti-v3-conf-list">
                    <div className="cti-v3-conf-row"><Dot tone="call" size={5} /> {call.customer?.name} (customer)</div>
                    <div className="cti-v3-conf-row"><Dot tone="good" size={5} /> You</div>
                    {call.parties.map((p) => (
                      <div className="cti-v3-conf-row" key={p.name}>
                        <Dot tone="good" size={5} />
                        <span className="cti-v3-conf-name">{p.name}</span>
                        <button
                          onClick={() => phone.dropConference(p.name)}
                          className="cti-v3-conf-drop"
                          title="Drop"
                        >
                          <X size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="cti-v3-no-call">
              No active call
              <button onClick={phone.simulateInbound} className="cti-v3-simulate">
                Simulate inbound call
              </button>
            </div>
          )}
        </div>

        {/* controls */}
        <div className="cti-v3-section">
          {call.status === 'ringing' ? (
            <div className="cti-v3-ring-grid">
              <button onClick={phone.answer} className="cti-v3-ring-btn answer">
                <Phone size={14} /> Answer
              </button>
              <button onClick={phone.endWrap} className="cti-v3-ring-btn reject">
                <PhoneOff size={14} /> Reject
              </button>
            </div>
          ) : (
            <>
              <div className="cti-v3-ctrl-grid">
                <Ctl icon={call.muted ? MicOff : Mic} label="Mute" active={call.muted} disabled={!canControl} onClick={phone.toggleMute} />
                <Ctl icon={call.held ? Play : Pause} label="Hold" active={call.held} disabled={!canControl} onClick={phone.toggleHold} />
                <Ctl icon={ArrowLeftRight} label="Transfer" disabled={!canControl} onClick={() => setPicker('transfer')} />
                <Ctl icon={UserPlus} label="Conf" disabled={!canControl} onClick={() => setPicker('conference')} />
                <Ctl icon={Grid3x3} label="Keypad" active={padOpen} disabled={!canControl} onClick={() => setPadOpen((o) => !o)} />
              </div>
              <button
                onClick={call.status === 'wrapup' ? phone.endWrap : active ? phone.hangup : () => setPadOpen(true)}
                className={`cti-v3-mainbtn ${active ? 'danger' : 'primary'}`}
              >
                {active
                  ? (<><PhoneOff size={14} /> {call.status === 'wrapup' ? 'Finish wrap-up' : 'Hang up'}</>)
                  : (<><PhoneCall size={14} /> New call</>)}
              </button>
            </>
          )}
        </div>

        {/* dialpad */}
        {(padOpen || call.status === 'idle') && (
          <div className="cti-v3-section">
            <div className="cti-v3-pad-display">
              <input
                value={live ? phone.dtmf : phone.dialpad}
                onChange={(e) => phone.setDialpad(e.target.value)}
                readOnly={live}
                placeholder={live ? 'DTMF tones' : 'Enter number'}
                className="num"
              />
              {!live && phone.dialpad && (
                <button
                  onClick={() => phone.setDialpad(phone.dialpad.slice(0, -1))}
                  className="cti-v3-pad-del"
                >
                  <Delete size={13} />
                </button>
              )}
            </div>
            <div className="cti-v3-pad-grid">
              {KEYS.map((k) => (
                <button
                  key={k}
                  onClick={() => (live ? phone.sendDtmf(k) : phone.setDialpad(phone.dialpad + k))}
                  className="cti-v3-pad-key num"
                >
                  {k}
                </button>
              ))}
            </div>
            {!live && (
              <button
                onClick={() => phone.dial()}
                disabled={!phone.dialpad}
                className="cti-v3-pad-call"
              >
                <PhoneCall size={13} /> Call
              </button>
            )}
          </div>
        )}

        {/* recent calls */}
        <div className="cti-v3-recent">
          <div className="cti-v3-recent-head">
            <span>Recent calls</span>
            <Kbd>{RECENT_CALLS.length}</Kbd>
          </div>
          <div className="cti-v3-recent-list">
            {RECENT_CALLS.map((r, i) => (
              <button
                key={i}
                onClick={() => !active && phone.dial(r.number)}
                className="cti-v3-recent-row"
              >
                {r.dir === 'in' ? (
                  <PhoneIncoming size={11} className="cti-v3-muted-ico" />
                ) : (
                  <PhoneOutgoing size={11} className="cti-v3-muted-ico" />
                )}
                <span className="cti-v3-recent-body">
                  <span className="cti-v3-recent-name">{r.name}</span>
                  <span className="num cti-v3-recent-sub">{r.at} · {r.dur}</span>
                </span>
                <span className={`cti-v3-recent-dispo ${TONE[r.tone].text}`}>{r.dispo}</span>
              </button>
            ))}
          </div>
        </div>
      </aside>
      {picker && <ContactPicker mode={picker} phone={phone} onClose={() => setPicker(null)} />}
    </>
  );
}

/* ================================================================== */
/*  Contact picker (Transfer / Conference)                              */
/* ================================================================== */

function ContactPicker({ mode, phone, onClose }) {
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const internal = CONTACTS.internal.filter((c) => !needle
    || c.name.toLowerCase().includes(needle)
    || c.role.toLowerCase().includes(needle)
    || c.ext.includes(needle));
  const queues = CONTACTS.queues.filter((c) => !needle || c.name.toLowerCase().includes(needle));

  const act = (target, kind) => {
    if (mode === 'conference') phone.addConference(target);
    else if (kind === 'blind') phone.completeTransfer(target, 'blind');
    else phone.startConsult(target);
    onClose();
  };

  return (
    <div className="cti-v3-modal-backdrop" onClick={onClose}>
      <div className="cti-v3-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cti-v3-modal-head">
          {mode === 'conference' ? <Users size={15} /> : <ArrowLeftRight size={15} />}
          <div className="cti-v3-modal-body">
            <div className="cti-v3-modal-title">
              {mode === 'conference' ? 'Add to conference' : 'Transfer call'}
            </div>
            <div className="cti-v3-modal-sub">
              {mode === 'conference' ? 'The customer stays on the line' : 'Warm consults first · blind releases immediately'}
            </div>
          </div>
          <button onClick={onClose} className="cti-v3-icon-btn">
            <X size={15} />
          </button>
        </div>

        <div className="cti-v3-modal-search">
          <Search size={13} className="cti-v3-muted-ico" />
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search agents, skills, queues or extension…"
          />
        </div>

        <div className="cti-v3-modal-scroll">
          <div className="cti-v3-modal-eyebrow">People & skills</div>
          {internal.map((c) => {
            const busy = c.state !== 'available';
            return (
              <div key={c.ext} className="cti-v3-picker-row">
                <Dot tone={c.state === 'available' ? 'good' : c.state === 'onCall' ? 'call' : 'warn'} size={6} />
                <span className="cti-v3-picker-body">
                  <span className="cti-v3-picker-name">{c.name}</span>
                  <span className="cti-v3-picker-sub">{c.role} · x{c.ext}</span>
                </span>
                {mode === 'conference' ? (
                  <button onClick={() => act(c, 'conf')} className="cti-v3-picker-btn primary">Add</button>
                ) : (
                  <span className="cti-v3-picker-btns">
                    <button onClick={() => act(c, 'warm')} className="cti-v3-picker-btn primary">Warm</button>
                    <button
                      onClick={() => act(c, 'blind')}
                      disabled={busy}
                      title={busy ? 'Target is not available' : 'Release immediately'}
                      className="cti-v3-picker-btn"
                    >
                      Blind
                    </button>
                  </span>
                )}
              </div>
            );
          })}

          <div className="cti-v3-modal-eyebrow">Queues</div>
          {queues.map((c) => (
            <div key={c.name} className="cti-v3-picker-row">
              <Dot tone={c.waiting > 8 ? 'crit' : c.waiting > 0 ? 'warn' : 'good'} size={6} />
              <span className="cti-v3-picker-body">
                <span className="num cti-v3-picker-name">{c.name}</span>
                <span className="num cti-v3-picker-sub">{c.waiting} waiting · {c.agents} agents</span>
              </span>
              <button
                onClick={() => act({ name: c.name, role: 'Queue', ext: '—', state: 'available' }, 'blind')}
                className="cti-v3-picker-btn primary"
              >
                {mode === 'conference' ? 'Add' : 'Send'}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ================================================================== */
/*  Panels                                                             */
/* ================================================================== */

function CustomerPanel({ customer, meta, sec, status }) {
  if (!customer) return null;
  const live = status === 'connected';

  const facts = [
    { icon: BadgeCheck, label: 'Customer ID', value: customer.customerId },
    { icon: Star, label: 'Plan', value: customer.plan },
    { icon: Clock, label: 'Tenure', value: customer.tenure },
    { icon: MapPin, label: 'City', value: customer.city },
    { icon: MessageSquareQuote, label: 'Language', value: customer.language },
    { icon: Mail, label: 'Email', value: customer.email },
  ];

  return (
    <Panel>
      <div className="cti-v3-customer-head">
        <span className="cti-v3-customer-avatar">
          {customer.name.split(' ').map((w) => w[0]).slice(0, 2).join('')}
        </span>
        <div className="cti-v3-customer-body">
          <div className="cti-v3-customer-title">
            <h2>{customer.name}</h2>
            {customer.segment && <Pill tone="good">{customer.segment}</Pill>}
            {customer.riskScore >= 70 && <Pill tone="crit">Churn risk {customer.riskScore}</Pill>}
          </div>
          <div className="num cti-v3-customer-sub">
            <span className="cti-v3-customer-num">{customer.number}</span>
            <span>{customer.altNumber}</span>
            <span>ARPU {customer.arpu}</span>
            <span>Balance {customer.balance}</span>
          </div>
        </div>
        <div className="cti-v3-customer-right">
          <div className="num cti-v3-customer-timer">{mmssLong(sec)}</div>
          <div className="cti-v3-customer-timer-cap">
            {meta?.direction === 'Outbound' ? <PhoneOutgoing size={10} /> : <PhoneIncoming size={10} />}
            {meta?.direction} · {live ? 'talking' : 'wrap-up'}
          </div>
        </div>
      </div>

      <div className="cti-v3-facts">
        {facts.map((f) => (
          <div key={f.label}>
            <div className="cti-v3-fact-label">
              <f.icon size={9} />
              {f.label}
            </div>
            <div className="cti-v3-fact-value" title={f.value}>{f.value}</div>
          </div>
        ))}
      </div>

      <div className="cti-v3-customer-tags">
        {customer.tags?.map((t) => (
          <Pill key={t} tone={t === 'Churn risk' ? 'crit' : t === 'Repeat caller' ? 'warn' : 'off'}>{t}</Pill>
        ))}
        <span className="cti-v3-consent">{customer.consent}</span>
      </div>
    </Panel>
  );
}

function ScriptPanel() {
  const [i, setI] = useState(0);
  const [done, setDone] = useState({});
  const [tab, setTab] = useState('script');
  const step = SCRIPT.steps[i];
  const last = SCRIPT.steps.length - 1;

  const KIND = {
    say: { label: 'Say', tone: 'call' },
    ask: { label: 'Ask', tone: 'good' },
    check: { label: 'Verify', tone: 'warn' },
  };

  return (
    <Panel>
      <div className="cti-v3-panel-head">
        <span className="cti-v3-panel-mark s-good"><MessageSquareQuote size={13} /></span>
        <div className="cti-v3-panel-body">
          <div className="cti-v3-panel-title">{SCRIPT.name}</div>
          <div className="cti-v3-panel-sub">{SCRIPT.version}</div>
        </div>
        <Tabs
          size="sm"
          value={tab}
          onChange={setTab}
          tabs={[
            { key: 'script', label: 'Script' },
            { key: 'obj', label: 'Objections' },
          ]}
        />
      </div>

      {tab === 'script' ? (
        <>
          <div className="cti-v3-script-progress">
            {SCRIPT.steps.map((s, n) => (
              <button
                key={n}
                onClick={() => setI(n)}
                title={s.title}
                className={`cti-v3-progress-seg ${n < i ? 'done' : n === i ? 'active' : ''}`}
              />
            ))}
            <span className="num cti-v3-progress-count">
              {i + 1}/{SCRIPT.steps.length}
            </span>
          </div>

          <div className="cti-v3-script-body">
            <div className="cti-v3-script-badge">
              <Pill tone={KIND[step.kind].tone}>{KIND[step.kind].label}</Pill>
              <span className="cti-v3-script-title">{step.title}</span>
            </div>

            <p className="cti-v3-script-text">{step.body}</p>

            {step.hint && (
              <div className="cti-v3-script-hint">
                <Lightbulb size={12} className="t-warn" />
                <span>{step.hint}</span>
              </div>
            )}

            {step.checks && (
              <div className="cti-v3-script-checks">
                {step.checks.map((c) => {
                  const on = done[step.title + c];
                  return (
                    <button
                      key={c}
                      onClick={() => setDone((d) => ({ ...d, [step.title + c]: !on }))}
                      className="cti-v3-script-check"
                    >
                      <span className={`cti-v3-check-box ${on ? 'on' : ''}`}>
                        {on && <Check size={10} strokeWidth={3} />}
                      </span>
                      <span className={`cti-v3-check-label ${on ? 'done' : ''}`}>{c}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {step.branches && (
              <div className="cti-v3-script-branches">
                <div className="cti-v3-caption">Customer response</div>
                <div className="cti-v3-branch-list">
                  {step.branches.map((b) => (
                    <button
                      key={b.label}
                      onClick={() => setI(Math.min(last, i + 1))}
                      className="cti-v3-branch-btn"
                    >
                      {b.label}
                      <span className="cti-v3-branch-next">→ {b.next}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="cti-v3-script-foot">
            <button
              onClick={() => setI(Math.max(0, i - 1))}
              disabled={i === 0}
              className="cti-v3-foot-btn"
            >
              <ChevronLeft size={12} /> Back
            </button>
            <button
              onClick={() => setI(Math.min(last, i + 1))}
              disabled={i === last}
              className="cti-v3-foot-btn primary"
            >
              Next step <ChevronRight size={12} />
            </button>
            <span className="cti-v3-foot-hint">
              <Sparkles size={10} /> Assist: mention the open ticket before pricing
            </span>
          </div>
        </>
      ) : (
        <div className="cti-v3-obj-list">
          {SCRIPT.objections.map((o) => (
            <div key={o.q} className="cti-v3-obj-row">
              <div className="cti-v3-obj-q">
                <CircleDot size={11} className="t-crit" />
                “{o.q}”
              </div>
              <p className="cti-v3-obj-a">{o.a}</p>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

function DispositionPanel({ status, onSave }) {
  const [pick, setPick] = useState(null);
  const [note, setNote] = useState('');
  const [when, setWhen] = useState('19:00');
  const [saved, setSaved] = useState(false);
  const [showPrev, setShowPrev] = useState(true);

  const chosen = DISPOSITIONS_LIST.find((d) => d.label === pick);
  const needsCb = chosen?.needsCallback;

  return (
    <Panel>
      <div className="cti-v3-panel-head">
        <span className="cti-v3-panel-mark s-warn"><NotebookPen size={13} /></span>
        <div className="cti-v3-panel-body">
          <div className="cti-v3-panel-title">Disposition</div>
          <div className="cti-v3-panel-sub">Opens automatically with every call</div>
        </div>
        {status === 'wrapup' && <Pill tone="warn">Required to close wrap-up</Pill>}
        {saved && <Pill tone="good">Saved</Pill>}
      </div>

      <div className="cti-v3-prev">
        <button
          onClick={() => setShowPrev((s) => !s)}
          className="cti-v3-prev-toggle"
        >
          <History size={10} />
          Previously saved on this caller ({SAVED_NOTES.length})
          <ChevronRight size={11} className={`cti-v3-prev-caret ${showPrev ? 'open' : ''}`} />
        </button>
        {showPrev && (
          <div className="cti-v3-prev-list">
            {SAVED_NOTES.map((n, i) => (
              <div key={i} className="cti-v3-prev-item">
                <div className="num cti-v3-prev-meta">{n.at} · {n.by}</div>
                <div className="cti-v3-prev-text">{n.text}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="cti-v3-dispo-body">
        <div className="cti-v3-caption">Outcome</div>
        <div className="cti-v3-dispo-chips">
          {DISPOSITIONS_LIST.map((d) => {
            const on = pick === d.label;
            return (
              <button
                key={d.label}
                onClick={() => { setPick(d.label); setSaved(false); }}
                className={`cti-v3-dispo-chip ${on ? `on ${TONE[d.tone].soft} ${TONE[d.tone].text}` : ''}`}
              >
                {d.label}
              </button>
            );
          })}
        </div>

        {needsCb && (
          <div className="cti-v3-dispo-cb">
            <div>
              <div className="cti-v3-caption">Callback time</div>
              <CalendarTimeInput
                value={when}
                onChange={(e) => setWhen(e)}
                className="cti-v3-time-input num"
              />
            </div>
            <span className="cti-v3-cb-hint">
              Caller prefers after 19:00 — from his saved notes
            </span>
          </div>
        )}

        <div className="cti-v3-dispo-notes">
          <div className="cti-v3-caption">New details about this caller</div>
          <textarea
            value={note}
            onChange={(e) => { setNote(e.target.value); setSaved(false); }}
            rows={3}
            placeholder="What was discussed, what was agreed, what the next agent needs to know…"
          />
        </div>

        <div className="cti-v3-dispo-actions">
          <button
            disabled={!pick}
            onClick={() => { setSaved(true); onSave?.(); }}
            className="cti-v3-mainbtn primary sm"
          >
            Save & close
          </button>
          <button
            disabled={!pick}
            onClick={() => setSaved(true)}
            className="cti-v3-mainbtn sm"
          >
            Save & next call
          </button>
          {!pick && <span className="cti-v3-hint-text">Pick an outcome to enable saving</span>}
        </div>
      </div>
    </Panel>
  );
}

function HistoryPanel() {
  return (
    <Panel>
      <div className="cti-v3-panel-head">
        <History size={13} className="cti-v3-muted-ico" />
        <span className="cti-v3-panel-title">Call history</span>
        <span className="num cti-v3-panel-count">{CALL_HISTORY.length} interactions</span>
      </div>
      <div className="cti-v3-hist-list">
        {CALL_HISTORY.map((h, i) => (
          <div key={i} className="cti-v3-hist-row">
            <div className="cti-v3-hist-line">
              {h.dir === 'Outbound'
                ? <PhoneOutgoing size={10} className="cti-v3-muted-ico" />
                : <PhoneIncoming size={10} className="cti-v3-muted-ico" />}
              <span className="num cti-v3-hist-at">{h.at}</span>
              <span className={`cti-v3-hist-dispo ${TONE[h.tone].text}`}>{h.dispo}</span>
            </div>
            <div className="cti-v3-hist-agent">
              <span>{h.agent}</span>
              <span className="num cti-v3-hist-dur">{h.dur}</span>
            </div>
            <p className="cti-v3-hist-note">{h.note}</p>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function ContactsPanel({ phone }) {
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const match = (s) => !needle || String(s || '').toLowerCase().includes(needle);
  const ext = CONTACTS.external.filter((c) => match(c.name) || match(c.number) || match(c.city));
  const intl = CONTACTS.internal.filter((c) => match(c.name) || match(c.role) || match(c.ext));

  return (
    <Panel>
      <div className="cti-v3-panel-head">
        <Search size={13} className="cti-v3-muted-ico" />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search contacts…"
          className="cti-v3-search-input"
        />
      </div>
      <div className="cti-v3-contacts-list">
        <div className="cti-v3-eyebrow">Customers</div>
        {ext.map((c) => (
          <button
            key={c.number}
            onClick={() => phone.dial(c.number)}
            className="cti-v3-contact-row"
          >
            <User size={11} className="cti-v3-muted-ico" />
            <span className="cti-v3-contact-body">
              <span className="cti-v3-contact-name">{c.name}</span>
              <span className="num cti-v3-contact-sub">{c.number} · {c.city}</span>
            </span>
            <span className="cti-v3-contact-tag">{c.tag}</span>
            <Phone size={11} className="cti-v3-contact-dial" />
          </button>
        ))}

        <div className="cti-v3-eyebrow cti-v3-eyebrow-2">Internal</div>
        {intl.map((c) => (
          <div key={c.ext} className="cti-v3-contact-row static">
            <Dot tone={c.state === 'available' ? 'good' : c.state === 'onCall' ? 'call' : 'warn'} size={6} />
            <span className="cti-v3-contact-body">
              <span className="cti-v3-contact-name">{c.name}</span>
              <span className="num cti-v3-contact-sub">{c.role} · x{c.ext}</span>
            </span>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function CallDetailsPanel({ meta, sec, call }) {
  if (!meta) {
    return (
      <Panel>
        <div className="cti-v3-panel-empty">No call in progress.</div>
      </Panel>
    );
  }
  const rows = [
    ['Call ID', meta.callId],
    ['Direction', meta.direction],
    ['Queue', meta.queue],
    ['Campaign', meta.campaign],
    ['Routed by', meta.routedBy],
    ['Queue wait', meta.waitedSec + 's'],
    ['Talk time', mmssLong(sec)],
    ['Attempt', '#' + meta.attempt],
    ['Priority', meta.priority],
    ['Trunk', meta.trunk],
    ['Codec', meta.codec],
    ['MOS', String(meta.mos)],
  ];
  return (
    <Panel>
      <div className="cti-v3-panel-head">
        <Signal size={13} className="cti-v3-muted-ico" />
        <span className="cti-v3-panel-title">Call details</span>
        <Pill tone={call.held ? 'warn' : 'crit'}>
          <span className="pulse cti-v3-rec-dot" />
          {meta.recording}
        </Pill>
      </div>
      <div className="cti-v3-details-grid">
        {rows.map(([k, v]) => (
          <div key={k}>
            <div className="cti-v3-detail-key">{k}</div>
            <div className="num cti-v3-detail-value" title={v}>{v}</div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

/* ================================================================== */
/*  UI primitives                                                      */
/* ================================================================== */

function Panel({ className = '', children }) {
  return <div className={`cti-v3-panel ${className}`}>{children}</div>;
}

function LiveDot({ tone = 'good', label }) {
  return (
    <span className="cti-v3-livedot-wrap">
      <span className={`pulse cti-v3-livedot ${TONE[tone].bg}`} />
      {label && <span className="cti-v3-livedot-label">{label}</span>}
    </span>
  );
}

function Dot({ tone = 'off', size = 6 }) {
  return (
    <span
      className={`cti-v3-dot ${TONE[tone].bg}`}
      style={{ width: size, height: size }}
    />
  );
}

function Pill({ tone = 'off', children }) {
  return (
    <span className={`cti-v3-pill ${TONE[tone].soft} ${TONE[tone].text}`}>
      {children}
    </span>
  );
}

function Kbd({ children }) {
  return <kbd className="cti-v3-kbd num">{children}</kbd>;
}

function MiniBar({ value, max, tone = 'call', width = 56, showValue }) {
  const pct = Math.max(0, Math.min(100, (value / max) * 100));
  return (
    <span className="cti-v3-minibar-wrap">
      <span className="cti-v3-minibar-track" style={{ width }}>
        <span className={`cti-v3-minibar-fill ${TONE[tone].bg}`} style={{ width: pct + '%' }} />
      </span>
      {showValue && <span className="num cti-v3-minibar-val">{showValue}</span>}
    </span>
  );
}

function Tabs({ tabs, value, onChange, size = 'md' }) {
  return (
    <div className={`cti-v3-tabs ${size === 'sm' ? 'sm' : ''}`}>
      {tabs.map((t) => (
        <button
          key={t.key}
          onClick={() => onChange(t.key)}
          className={`cti-v3-tab ${t.key === value ? 'active' : ''}`}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

function SectionHead({ eyebrow, title, sub, right }) {
  return (
    <div className="cti-v3-sec-head">
      <div className="cti-v3-sec-body">
        {eyebrow && <div className="cti-v3-sec-eyebrow">{eyebrow}</div>}
        <h2 className="cti-v3-sec-title">{title}</h2>
        {sub && <p className="cti-v3-sec-sub">{sub}</p>}
      </div>
      {right && <div>{right}</div>}
    </div>
  );
}

function Th({ children }) {
  return <th className="cti-v3-th">{children}</th>;
}
function Td({ children, className = '' }) {
  return <td className={`cti-v3-td ${className}`}>{children}</td>;
}

/** Segmented Light / System / Dark control that writes to the app theme store. */
function ThemeToggle({ mode, setMode }) {
  const opts = [
    { key: 'light', icon: Sun, label: 'Light' },
    { key: 'system', icon: Monitor, label: 'System' },
    { key: 'dark', icon: Moon, label: 'Dark' },
  ];
  return (
    <div className="cti-v3-theme-toggle" role="group" aria-label="Theme">
      {opts.map((o) => {
        const Icon = o.icon;
        const active = mode === o.key;
        return (
          <button
            key={o.key}
            title={o.label}
            aria-label={o.label}
            aria-pressed={active}
            onClick={() => setMode(o.key)}
            className={`cti-v3-theme-tab ${active ? 'active' : ''}`}
          >
            <Icon size={13} />
          </button>
        );
      })}
    </div>
  );
}

/* ================================================================== */
/*  Scoped styles                                                      */
/* ================================================================== */

function StyleTag() {
  return (
    <style dangerouslySetInnerHTML={{ __html: `
      /* Bind our internal panel / ink / tone tokens to the app's shadcn
       * HSL tokens (--background, --foreground, --primary, --card, --muted,
       * --border, --destructive, --success, --warning). The page follows
       * whichever theme the user picked in the theme sheet — light, dark,
       * or system — with no per-page toggle. */
      .cti-v3 {
        --panel: hsl(var(--card));
        --panel-2: hsl(var(--muted));
        --line: hsl(var(--border));
        --line-strong: hsl(var(--border) / 0.85);
        --ink: hsl(var(--foreground));
        --ink-2: hsl(var(--muted-foreground));
        --ink-3: hsl(var(--muted-foreground) / 0.72);
        --good: hsl(var(--success));
        --call: hsl(var(--primary));
        --warn: hsl(var(--warning));
        --crit: hsl(var(--destructive));
        --off: hsl(var(--muted-foreground) / 0.55);

        /* soft (tinted) surfaces — same hue as the tone, low alpha */
        --s-good-bg: hsl(var(--success) / 0.12);
        --s-call-bg: hsl(var(--primary) / 0.12);
        --s-warn-bg: hsl(var(--warning) / 0.12);
        --s-crit-bg: hsl(var(--destructive) / 0.12);
        --s-off-bg: hsl(var(--muted-foreground) / 0.12);

        --shadow: 0 1px 2px hsl(var(--foreground) / 0.06), 0 6px 20px hsl(var(--foreground) / 0.08);
        --shadow-lg: 0 8px 30px hsl(var(--foreground) / 0.18);

        /* Embossed depth — light mode only. Combines a soft top highlight
         * (inset white) with a shallow drop shadow so panels + interactive
         * surfaces lift off the ground. Overridden to flat in .dark below. */
        --emboss-panel:
          0 1px 0 hsl(0 0% 100% / 0.9) inset,
          0 1px 2px hsl(var(--foreground) / 0.06),
          0 8px 24px hsl(var(--foreground) / 0.06);
        --emboss-btn:
          0 1px 0 hsl(0 0% 100% / 0.85) inset,
          0 -1px 0 hsl(var(--foreground) / 0.08) inset,
          0 1px 2px hsl(var(--foreground) / 0.08),
          0 2px 6px hsl(var(--foreground) / 0.08);
        --emboss-btn-hover:
          0 1px 0 hsl(0 0% 100% / 0.95) inset,
          0 -1px 0 hsl(var(--foreground) / 0.10) inset,
          0 2px 4px hsl(var(--foreground) / 0.10),
          0 6px 14px hsl(var(--foreground) / 0.12);
        --emboss-pressed:
          0 1px 2px hsl(var(--foreground) / 0.16) inset,
          0 -1px 0 hsl(0 0% 100% / 0.5) inset;
        --emboss-chip:
          0 1px 0 hsl(0 0% 100% / 0.7) inset,
          0 1px 1px hsl(var(--foreground) / 0.06);
        --emboss-key:
          0 1px 0 hsl(0 0% 100% / 0.9) inset,
          0 -1px 0 hsl(var(--foreground) / 0.10) inset,
          0 1px 0 hsl(var(--foreground) / 0.08),
          0 2px 4px hsl(var(--foreground) / 0.08);
        --emboss-key-press:
          0 1px 3px hsl(var(--foreground) / 0.20) inset,
          0 -1px 0 hsl(0 0% 100% / 0.4) inset;

        position: fixed; inset: 0; z-index: 1000;
        display: flex; min-height: 100vh; max-height: 100vh;
        background: hsl(var(--background)); color: var(--ink);
        font-family: ui-sans-serif, -apple-system, sans-serif;
        font-size: 13px; letter-spacing: -0.01em; overflow: hidden;
      }
      .cti-v3 * { box-sizing: border-box; }
      .cti-v3 .num { font-family: ui-monospace, monospace; font-variant-numeric: tabular-nums; letter-spacing: -0.01em; }
      .cti-v3 button { font-family: inherit; }
      .cti-v3 button:not(:disabled) { cursor: pointer; }

      /* Tone helpers */
      .cti-v3 .t-good { color: var(--good); }
      .cti-v3 .t-call { color: var(--call); }
      .cti-v3 .t-warn { color: var(--warn); }
      .cti-v3 .t-crit { color: var(--crit); }
      .cti-v3 .t-off { color: var(--off); }
      .cti-v3 .bg-good { background: var(--good); }
      .cti-v3 .bg-call { background: var(--call); }
      .cti-v3 .bg-warn { background: var(--warn); }
      .cti-v3 .bg-crit { background: var(--crit); }
      .cti-v3 .bg-off { background: var(--off); }
      .cti-v3 .s-good { background: var(--s-good-bg); }
      .cti-v3 .s-call { background: var(--s-call-bg); }
      .cti-v3 .s-warn { background: var(--s-warn-bg); }
      .cti-v3 .s-crit { background: var(--s-crit-bg); }
      .cti-v3 .s-off { background: var(--s-off-bg); }

      /* Dark mode: strip the embossed shadows — a light inset highlight on a
       * dark surface reads as a dirty edge, not depth. Fall back to flat
       * borders + the base drop shadow. */
      :root.dark .cti-v3 {
        --emboss-panel: 0 1px 2px hsl(0 0% 0% / 0.35);
        --emboss-btn: none;
        --emboss-btn-hover: none;
        --emboss-pressed: 0 1px 2px hsl(0 0% 0% / 0.40) inset;
        --emboss-chip: none;
        --emboss-key: 0 1px 2px hsl(0 0% 0% / 0.30);
        --emboss-key-press: 0 1px 2px hsl(0 0% 0% / 0.40) inset;
      }

      /* Apply the embossed shadows. Because the vars flatten in dark mode,
       * a single rule covers both themes without a media / class split. */
      .cti-v3 .cti-v3-panel { box-shadow: var(--emboss-panel); }
      .cti-v3 .cti-v3-modal { box-shadow: var(--emboss-panel), var(--shadow-lg); }
      .cti-v3 .cti-v3-kpi { box-shadow: var(--emboss-panel); }

      .cti-v3 .cti-v3-btn,
      .cti-v3 .cti-v3-foot-btn,
      .cti-v3 .cti-v3-branch-btn,
      .cti-v3 .cti-v3-dispo-chip,
      .cti-v3 .cti-v3-picker-btn,
      .cti-v3 .cti-v3-consult-btn,
      .cti-v3 .cti-v3-ctl:not(:disabled),
      .cti-v3 .cti-v3-mini-state,
      .cti-v3 .cti-v3-device-tab,
      .cti-v3 .cti-v3-tab,
      .cti-v3 .cti-v3-theme-tab,
      .cti-v3 .cti-v3-simulate,
      .cti-v3 .cti-v3-time-input,
      .cti-v3 .cti-v3-prev-item,
      .cti-v3 .cti-v3-meta-chip,
      .cti-v3 .cti-v3-conf-box,
      .cti-v3 .cti-v3-consult { box-shadow: var(--emboss-chip); }

      .cti-v3 .cti-v3-btn:hover,
      .cti-v3 .cti-v3-foot-btn:hover:not(:disabled),
      .cti-v3 .cti-v3-branch-btn:hover,
      .cti-v3 .cti-v3-picker-btn:hover:not(:disabled) { box-shadow: var(--emboss-btn-hover); }

      .cti-v3 .cti-v3-btn:active,
      .cti-v3 .cti-v3-foot-btn:active:not(:disabled),
      .cti-v3 .cti-v3-branch-btn:active,
      .cti-v3 .cti-v3-picker-btn:active:not(:disabled),
      .cti-v3 .cti-v3-ctl:active:not(:disabled),
      .cti-v3 .cti-v3-tab.active,
      .cti-v3 .cti-v3-device-tab.active,
      .cti-v3 .cti-v3-theme-tab.active { box-shadow: var(--emboss-pressed); }

      /* Primary + danger call-to-action buttons — slightly deeper emboss */
      .cti-v3 .cti-v3-mainbtn,
      .cti-v3 .cti-v3-ring-btn,
      .cti-v3 .cti-v3-pad-call,
      .cti-v3 .cti-v3-foot-btn.primary,
      .cti-v3 .cti-v3-picker-btn.primary,
      .cti-v3 .cti-v3-consult-btn.primary { box-shadow: var(--emboss-btn); }
      .cti-v3 .cti-v3-mainbtn:hover:not(:disabled),
      .cti-v3 .cti-v3-ring-btn:hover,
      .cti-v3 .cti-v3-pad-call:hover:not(:disabled),
      .cti-v3 .cti-v3-foot-btn.primary:hover:not(:disabled),
      .cti-v3 .cti-v3-picker-btn.primary:hover,
      .cti-v3 .cti-v3-consult-btn.primary:hover { box-shadow: var(--emboss-btn-hover); }
      .cti-v3 .cti-v3-mainbtn:active:not(:disabled),
      .cti-v3 .cti-v3-ring-btn:active,
      .cti-v3 .cti-v3-pad-call:active:not(:disabled) { box-shadow: var(--emboss-pressed); }

      /* Dialpad keys — the most tactile surface, keep them clearly keyboard-like */
      .cti-v3 .cti-v3-pad-key { box-shadow: var(--emboss-key); }
      .cti-v3 .cti-v3-pad-key:hover { box-shadow: var(--emboss-key), 0 3px 8px hsl(var(--foreground) / 0.10); }
      .cti-v3 .cti-v3-pad-key:active { box-shadow: var(--emboss-key-press); transform: translateY(0.5px); }

      .cti-v3 .pulse { position: relative; }
      .cti-v3 .pulse::after {
        content: ''; position: absolute; inset: -3px; border-radius: 50%;
        background: currentColor; opacity: 0.3; animation: cti-v3-pulse 1.6s ease-out infinite;
      }
      @keyframes cti-v3-pulse {
        0% { transform: scale(0.7); opacity: 0.4; }
        100% { transform: scale(1.8); opacity: 0; }
      }

      /* Panel */
      .cti-v3-panel {
        border-radius: 14px; border: 1px solid var(--line); background: var(--panel);
        box-shadow: var(--shadow); overflow: hidden;
      }
      .cti-v3-p-4 { padding: 16px; }

      /* Pill */
      .cti-v3-pill {
        display: inline-flex; align-items: center; gap: 6px;
        padding: 2px 8px; border-radius: 999px; font-size: 11px; font-weight: 500;
      }

      /* Dot */
      .cti-v3-dot { display: inline-block; flex-shrink: 0; border-radius: 50%; }

      /* Live dot */
      .cti-v3-livedot-wrap { display: inline-flex; align-items: center; gap: 6px; }
      .cti-v3-livedot { display: inline-block; width: 6px; height: 6px; border-radius: 50%; }
      .cti-v3-livedot-label { font-size: 10px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.06em; color: var(--ink-3); }

      /* Kbd */
      .cti-v3-kbd {
        border: 1px solid var(--line-strong); background: var(--panel-2);
        border-radius: 4px; padding: 1px 6px; font-size: 10px; font-weight: 500;
        color: var(--ink-3);
      }

      /* MiniBar */
      .cti-v3-minibar-wrap { display: inline-flex; align-items: center; gap: 8px; }
      .cti-v3-minibar-track { position: relative; display: inline-block; height: 6px; overflow: hidden; border-radius: 999px; background: var(--line); }
      .cti-v3-minibar-fill { position: absolute; inset: 0 auto 0 0; border-radius: 999px; transition: width 300ms; }
      .cti-v3-minibar-val { font-size: 12px; color: var(--ink-2); }

      /* Tabs */
      .cti-v3-tabs {
        display: inline-flex; gap: 2px; padding: 2px;
        border-radius: 8px; border: 1px solid var(--line); background: var(--panel-2);
      }
      .cti-v3-tab {
        padding: 6px 12px; border: none; background: transparent; color: var(--ink-2);
        font-size: 12px; font-weight: 500; border-radius: 6px; transition: all 120ms;
      }
      .cti-v3-tabs.sm .cti-v3-tab { padding: 4px 10px; font-size: 11px; }
      .cti-v3-tab:hover { color: var(--ink); }
      .cti-v3-tab.active { background: var(--panel); color: var(--ink); box-shadow: var(--shadow); }

      /* SectionHead */
      .cti-v3-sec-head { display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; margin-bottom: 12px; }
      .cti-v3-sec-body { min-width: 0; }
      .cti-v3-sec-eyebrow {
        display: flex; align-items: center; gap: 8px;
        font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.11em;
        color: var(--ink-3); margin-bottom: 4px;
      }
      .cti-v3-sec-title { margin: 0; font-size: 15px; font-weight: 600; letter-spacing: -0.01em; color: var(--ink); }
      .cti-v3-sec-sub { margin: 2px 0 0; font-size: 12px; color: var(--ink-2); }

      /* Table */
      .cti-v3-table-wrap { margin: 0 -16px -16px; overflow: auto; }
      .cti-v3-table { width: 100%; border-collapse: collapse; text-align: left; }
      .cti-v3-th {
        position: sticky; top: 0; z-index: 1;
        border-bottom: 1px solid var(--line); background: var(--panel);
        padding: 8px 12px; font-size: 10px; font-weight: 600;
        text-transform: uppercase; letter-spacing: 0.08em; color: var(--ink-3);
        white-space: nowrap;
      }
      .cti-v3-td {
        border-bottom: 1px solid var(--line); padding: 8px 12px;
        font-size: 13px; color: var(--ink); white-space: nowrap;
      }
      .cti-v3-table tbody tr { cursor: pointer; transition: background 120ms; }
      .cti-v3-table tbody tr:hover { background: var(--panel-2); }

      /* ============================================================= */
      /*  Dialer                                                        */
      /* ============================================================= */
      .cti-v3-dialer {
        flex-shrink: 0; display: flex; flex-direction: column;
        border-right: 1px solid var(--line); background: var(--panel);
        height: 100vh; overflow: hidden;
      }
      .cti-v3-dialer.collapsed { width: 60px; align-items: center; padding: 12px 0; gap: 8px; }
      .cti-v3-dialer.expanded { width: 292px; }

      .cti-v3-hairline { margin: 4px 0; height: 1px; width: 28px; background: var(--line); }

      .cti-v3-collapse-btn, .cti-v3-icon-btn {
        display: grid; place-items: center;
        width: 36px; height: 36px; border-radius: 10px;
        border: none; background: transparent; color: var(--ink-3);
        transition: all 120ms;
      }
      .cti-v3-collapse-btn:hover, .cti-v3-icon-btn:hover { background: var(--panel-2); color: var(--ink); }

      .cti-v3-mini-state {
        display: flex; flex-direction: column; align-items: center; gap: 4px;
        width: 46px; padding: 6px 4px; border-radius: 10px;
        border: 1px solid var(--line); background: var(--panel-2); color: var(--ink);
      }
      .cti-v3-mini-state.active { border-color: hsl(var(--primary) / 0.30); background: hsl(var(--primary) / 0.10); }
      .cti-v3-mini-timer { font-size: 9px; font-weight: 600; }

      .cti-v3-dialer-bottom { margin-top: auto; display: flex; flex-direction: column; align-items: center; gap: 8px; }
      .cti-v3-conf-count {
        display: grid; place-items: center; width: 24px; height: 24px; border-radius: 50%;
        background: hsl(var(--primary) / 0.15); color: var(--call); font-size: 10px; font-weight: 700;
      }

      /* Ctl button */
      .cti-v3-ctl {
        display: flex; align-items: center; justify-content: center; gap: 4px;
        border-radius: 10px; border: 1px solid var(--line);
        background: var(--panel-2); color: var(--ink-2);
        transition: all 150ms; font-weight: 500;
      }
      .cti-v3-ctl.compact { width: 36px; height: 36px; }
      .cti-v3-ctl:not(.compact) {
        height: 38px; flex-direction: column; gap: 2px; font-size: 9px;
      }
      .cti-v3-ctl:hover:not(:disabled) { border-color: var(--line-strong); color: var(--ink); }
      .cti-v3-ctl:disabled { opacity: 0.35; cursor: not-allowed; }
      .cti-v3-ctl.active { border-color: hsl(var(--primary) / 0.40); background: hsl(var(--primary) / 0.15); color: var(--call); }
      .cti-v3-ctl.danger { border-color: hsl(var(--destructive) / 0.30); background: hsl(var(--destructive) / 0.10); color: var(--crit); }
      .cti-v3-ctl.danger:hover { background: hsl(var(--destructive) / 0.20); }

      /* Dialer sections */
      .cti-v3-dialer-head {
        display: flex; align-items: center; gap: 8px;
        padding: 10px 12px; border-bottom: 1px solid var(--line);
      }
      .cti-v3-dialer-mark {
        display: grid; place-items: center; width: 24px; height: 24px;
        border-radius: 7px; background: hsl(var(--primary) / 0.12); color: var(--call);
      }
      .cti-v3-dialer-title { flex: 1; font-size: 12px; font-weight: 600; }

      .cti-v3-section { padding: 10px 12px; border-bottom: 1px solid var(--line); }
      .cti-v3-caption {
        font-size: 10px; font-weight: 600; text-transform: uppercase;
        letter-spacing: 0.1em; color: var(--ink-3); margin-bottom: 6px;
      }

      .cti-v3-device-caption {
        display: flex; align-items: center; justify-content: space-between; gap: 8px;
        margin-bottom: 6px;
      }
      .cti-v3-device-caption .cti-v3-caption { margin: 0; }
      .cti-v3-device-status {
        display: inline-flex; align-items: center; gap: 6px;
        font-size: 10px; font-weight: 500; color: var(--ink-2);
      }
      .cti-v3-device-status.muted { color: var(--ink-3); }
      .cti-v3-conn-dot {
        display: inline-block; width: 6px; height: 6px; border-radius: 50%;
      }
      .cti-v3-conn-dot.ok  { background: var(--good); box-shadow: 0 0 0 3px var(--s-good-bg); }
      .cti-v3-conn-dot.off { background: var(--off); }

      .cti-v3-device-tabs {
        display: flex; padding: 2px; border-radius: 8px;
        border: 1px solid var(--line); background: var(--panel-2);
      }
      .cti-v3-device-tab {
        flex: 1; display: inline-flex; align-items: center; justify-content: center;
        padding: 5px 4px; border: none; background: transparent;
        color: var(--ink-2); font-size: 10px; font-weight: 500;
        border-radius: 6px; transition: all 120ms; min-height: 24px;
      }
      .cti-v3-device-tab:not(:disabled):hover { color: var(--ink); }
      .cti-v3 .cti-v3-device-tabs .cti-v3-device-tab.active {
        background: var(--good); color: hsl(var(--success-foreground)); font-weight: 600;
        box-shadow:
          0 1px 0 hsl(0 0% 100% / 0.35) inset,
          0 -1px 0 hsl(var(--foreground) / 0.15) inset,
          0 1px 2px hsl(var(--foreground) / 0.12),
          0 3px 6px hsl(var(--success) / 0.28);
      }
      .cti-v3 .cti-v3-device-tabs .cti-v3-device-tab.active:not(:disabled):hover {
        color: hsl(var(--success-foreground)); filter: brightness(1.05);
      }
      .cti-v3-device-tab:disabled { cursor: not-allowed; }
      .cti-v3-device-tab.unavailable { opacity: 0.4; }
      .cti-v3-device-tab.unavailable::after {
        content: ''; position: relative; display: inline-block; width: 6px; height: 1px;
        background: currentColor; margin-left: 4px; opacity: 0.6;
      }

      .cti-v3 .cti-spin { animation: cti-v3-spin 0.85s linear infinite; }
      @keyframes cti-v3-spin { to { transform: rotate(360deg); } }

      .cti-v3-live-head { display: flex; align-items: center; justify-content: space-between; }
      .cti-v3-live-timer { font-size: 17px; font-weight: 600; letter-spacing: -0.02em; color: var(--ink); }
      .cti-v3-live-body { margin-top: 10px; }
      .cti-v3-live-caller { display: flex; align-items: center; gap: 6px; }
      .cti-v3-caller-name { font-size: 13px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .cti-v3-caller-num { margin-top: 2px; font-size: 11px; color: var(--ink-2); }
      .cti-v3-meta-row { margin-top: 6px; display: flex; flex-wrap: wrap; gap: 4px; }
      .cti-v3-meta-chip {
        border-radius: 4px; background: var(--panel-2); border: 1px solid var(--line);
        padding: 1px 6px; font-size: 9px; color: var(--ink-3);
      }

      .cti-v3-no-call {
        margin-top: 10px; padding: 12px; text-align: center;
        border: 1px dashed var(--line-strong); border-radius: 9px;
        font-size: 11px; color: var(--ink-3);
      }
      .cti-v3-simulate {
        display: block; margin-top: 6px; width: 100%;
        padding: 5px 8px; border: none; border-radius: 6px;
        background: hsl(var(--primary) / 0.12); color: var(--call);
        font-size: 10px; font-weight: 600;
      }

      /* Consult sub-card */
      .cti-v3-consult {
        margin-top: 10px; padding: 8px;
        border-radius: 9px; border: 1px solid hsl(var(--warning) / 0.30);
        background: hsl(var(--warning) / 0.07);
      }
      .cti-v3-consult-title { font-size: 10px; font-weight: 600; color: var(--warn); }
      .cti-v3-consult-actions { margin-top: 6px; display: flex; gap: 4px; }
      .cti-v3-consult-btn {
        border: none; border-radius: 6px; padding: 4px 8px;
        font-size: 10px; font-weight: 500;
        background: var(--panel-2); color: var(--ink-2);
        box-shadow: inset 0 0 0 1px var(--line);
      }
      .cti-v3-consult-btn.primary { background: hsl(var(--warning) / 0.15); color: var(--warn); box-shadow: none; flex: 1; font-weight: 600; }

      /* Conference roster */
      .cti-v3-conf-box {
        margin-top: 10px; padding: 8px;
        border: 1px solid var(--line); background: var(--panel-2); border-radius: 9px;
      }
      .cti-v3-conf-list { display: flex; flex-direction: column; gap: 4px; margin-top: 4px; }
      .cti-v3-conf-row { display: flex; align-items: center; gap: 6px; font-size: 10px; color: var(--ink-2); }
      .cti-v3-conf-name { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .cti-v3-conf-drop {
        display: grid; place-items: center; width: 18px; height: 18px;
        border: none; background: transparent; color: var(--ink-3); border-radius: 4px;
      }
      .cti-v3-conf-drop:hover { color: var(--crit); background: hsl(var(--destructive) / 0.10); }

      /* Controls */
      .cti-v3-ring-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
      .cti-v3-ring-btn {
        display: flex; align-items: center; justify-content: center; gap: 6px;
        height: 38px; border: none; border-radius: 10px;
        font-size: 12px; font-weight: 600;
      }
      .cti-v3-ring-btn.answer { background: var(--good); color: white; }
      .cti-v3-ring-btn.reject { background: hsl(var(--destructive) / 0.10); color: var(--crit); border: 1px solid hsl(var(--destructive) / 0.30); }

      .cti-v3-ctrl-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 6px; }
      .cti-v3-mainbtn {
        margin-top: 6px; display: flex; align-items: center; justify-content: center; gap: 8px;
        width: 100%; height: 38px; border: none; border-radius: 10px;
        font-size: 12px; font-weight: 600; transition: filter 120ms;
      }
      .cti-v3-mainbtn.sm { height: 32px; font-size: 12px; padding: 0 12px; width: auto; margin-top: 0; }
      .cti-v3-mainbtn.primary { background: var(--good); color: white; }
      .cti-v3-mainbtn.danger { background: var(--crit); color: white; }
      .cti-v3-mainbtn:hover:not(:disabled) { filter: brightness(1.1); }
      .cti-v3-mainbtn:disabled { opacity: 0.35; cursor: not-allowed; }
      .cti-v3-mainbtn.sm:not(.primary):not(.danger) { background: var(--panel-2); color: var(--ink-2); border: 1px solid var(--line); }

      /* Dialpad */
      .cti-v3-pad-display {
        display: flex; align-items: center; gap: 6px;
        padding: 8px 10px; margin-bottom: 6px;
        border-radius: 8px; background: var(--panel-2); border: 1px solid var(--line);
      }
      .cti-v3-pad-display input {
        flex: 1; min-width: 0; background: transparent; border: none; outline: none;
        color: var(--ink); font-size: 13px;
      }
      .cti-v3-pad-display input::placeholder { color: var(--ink-3); }
      .cti-v3-pad-del { border: none; background: transparent; color: var(--ink-3); }
      .cti-v3-pad-del:hover { color: var(--ink); }
      .cti-v3-pad-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px; }
      .cti-v3-pad-key {
        padding: 6px; border: 1px solid var(--line); border-radius: 8px;
        background: var(--panel-2); color: var(--ink); font-size: 13px; font-weight: 500;
      }
      .cti-v3-pad-key:hover { border-color: var(--line-strong); }
      .cti-v3-pad-key:active { background: var(--line); }
      .cti-v3-pad-call {
        margin-top: 6px; display: flex; align-items: center; justify-content: center; gap: 6px;
        width: 100%; height: 34px; border: none; border-radius: 9px;
        background: var(--good); color: white; font-size: 12px; font-weight: 600;
      }
      .cti-v3-pad-call:disabled { opacity: 0.35; }

      /* Recent */
      .cti-v3-recent { flex: 1; min-height: 0; overflow: hidden; display: flex; flex-direction: column; }
      .cti-v3-recent-head {
        position: sticky; top: 0; z-index: 1;
        display: flex; align-items: center; justify-content: space-between;
        padding: 8px 12px; border-bottom: 1px solid var(--line); background: var(--panel);
        font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; color: var(--ink-3);
      }
      .cti-v3-recent-list { overflow-y: auto; }
      .cti-v3-recent-row {
        display: flex; align-items: center; gap: 8px; width: 100%; text-align: left;
        padding: 8px 12px; border: none; border-bottom: 1px solid var(--line);
        background: transparent; color: var(--ink); transition: background 120ms;
      }
      .cti-v3-recent-row:hover { background: var(--panel-2); }
      .cti-v3-recent-body { flex: 1; min-width: 0; }
      .cti-v3-recent-name { display: block; font-size: 11px; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .cti-v3-recent-sub { display: block; font-size: 9px; color: var(--ink-3); }
      .cti-v3-recent-dispo { font-size: 9px; font-weight: 500; flex-shrink: 0; }

      .cti-v3-muted-ico { color: var(--ink-3); flex-shrink: 0; }

      /* ============================================================= */
      /*  Main + topbar                                                 */
      /* ============================================================= */
      .cti-v3-main { flex: 1; min-width: 0; display: flex; flex-direction: column; height: 100vh; overflow: hidden; }
      .cti-v3-topbar {
        display: flex; align-items: center; gap: 8px;
        padding: 12px 16px; border-bottom: 1px solid var(--line); background: var(--panel);
      }
      .cti-v3-agent-avatar {
        display: grid; place-items: center; width: 28px; height: 28px; border-radius: 50%;
        background: hsl(var(--success) / 0.10); color: var(--good); font-size: 11px; font-weight: 600;
      }
      .cti-v3-agent-body { min-width: 0; }
      .cti-v3-agent-name { display: flex; align-items: center; gap: 6px; font-size: 13px; font-weight: 600; }
      .cti-v3-agent-ext { font-size: 10px; font-weight: 400; color: var(--ink-3); }
      .cti-v3-agent-team { font-size: 10px; color: var(--ink-3); }
      .cti-v3-btn {
        display: inline-flex; align-items: center; gap: 6px;
        padding: 6px 12px; border-radius: 8px;
        border: 1px solid var(--line); background: var(--panel-2); color: var(--ink-2);
        font-size: 12px; font-weight: 500;
      }
      .cti-v3-btn:hover { color: var(--ink); border-color: var(--line-strong); }

      /* Theme toggle — three-way Light / System / Dark segmented control */
      .cti-v3-theme-toggle {
        display: inline-flex; gap: 2px; padding: 2px;
        border-radius: 8px; border: 1px solid var(--line); background: var(--panel-2);
      }
      .cti-v3-theme-tab {
        display: grid; place-items: center;
        width: 28px; height: 26px; border: none; border-radius: 6px;
        background: transparent; color: var(--ink-3); transition: all 120ms;
      }
      .cti-v3-theme-tab:hover { color: var(--ink); }
      .cti-v3-theme-tab.active {
        background: var(--panel); color: var(--ink);
        box-shadow: var(--shadow);
      }

      .cti-v3-work { flex: 1; min-height: 0; overflow: auto; padding: 16px; }

      /* Live grid */
      .cti-v3-live-grid { display: grid; gap: 16px; grid-template-columns: minmax(0, 1fr) 320px; }
      @media (min-width: 1600px) { .cti-v3-live-grid { grid-template-columns: minmax(0, 1fr) 360px; } }
      @media (max-width: 1200px) { .cti-v3-live-grid { grid-template-columns: 1fr; } }
      .cti-v3-center, .cti-v3-right { display: flex; flex-direction: column; gap: 16px; min-width: 0; }
      .cti-v3-right-tabs { display: flex; justify-content: flex-end; }

      /* Ringing empty */
      .cti-v3-ring-empty { padding: 24px; text-align: center; }
      .cti-v3-ring-title { font-size: 13px; font-weight: 600; }
      .cti-v3-ring-body { margin: 4px auto 0; max-width: 380px; font-size: 11px; color: var(--ink-2); }

      /* Customer panel */
      .cti-v3-customer-head {
        display: flex; align-items: flex-start; gap: 12px;
        padding: 16px; border-bottom: 1px solid var(--line);
      }
      .cti-v3-customer-avatar {
        display: grid; place-items: center; width: 44px; height: 44px; border-radius: 50%; flex-shrink: 0;
        background: hsl(var(--primary) / 0.10); color: var(--call); font-size: 15px; font-weight: 600;
      }
      .cti-v3-customer-body { flex: 1; min-width: 0; }
      .cti-v3-customer-title { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
      .cti-v3-customer-title h2 { margin: 0; font-size: 19px; font-weight: 600; letter-spacing: -0.02em; color: var(--ink); }
      .cti-v3-customer-sub { margin-top: 4px; display: flex; flex-wrap: wrap; row-gap: 2px; column-gap: 12px; font-size: 12px; color: var(--ink-2); }
      .cti-v3-customer-num { font-weight: 500; color: var(--ink); }
      .cti-v3-customer-right { flex-shrink: 0; text-align: right; }
      .cti-v3-customer-timer { font-size: 24px; font-weight: 600; line-height: 1; letter-spacing: -0.03em; color: var(--ink); }
      .cti-v3-customer-timer-cap {
        margin-top: 4px; display: inline-flex; align-items: center; gap: 6px;
        font-size: 10px; color: var(--ink-3);
      }

      .cti-v3-facts { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px 16px; padding: 16px; }
      @media (max-width: 700px) { .cti-v3-facts { grid-template-columns: 1fr 1fr; } }
      .cti-v3-fact-label {
        display: flex; align-items: center; gap: 4px;
        font-size: 10px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.07em; color: var(--ink-3);
      }
      .cti-v3-fact-value {
        margin-top: 2px; font-size: 12px; font-weight: 500; color: var(--ink);
        overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      }

      .cti-v3-customer-tags {
        display: flex; flex-wrap: wrap; align-items: center; gap: 6px;
        padding: 10px 16px; border-top: 1px solid var(--line);
      }
      .cti-v3-consent { margin-left: auto; font-size: 10px; color: var(--ink-3); }

      /* Script panel */
      .cti-v3-panel-head {
        display: flex; align-items: center; flex-wrap: wrap; gap: 8px;
        padding: 10px 16px; border-bottom: 1px solid var(--line);
      }
      .cti-v3-panel-mark {
        display: grid; place-items: center; width: 24px; height: 24px; border-radius: 7px;
        color: currentColor;
      }
      .cti-v3-panel-mark.s-good { color: var(--good); }
      .cti-v3-panel-mark.s-warn { color: var(--warn); }
      .cti-v3-panel-body { min-width: 0; flex: 1; }
      .cti-v3-panel-title { font-size: 12px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .cti-v3-panel-sub { font-size: 10px; color: var(--ink-3); }
      .cti-v3-panel-count { font-size: 10px; color: var(--ink-3); }
      .cti-v3-panel-empty { padding: 16px; text-align: center; font-size: 11px; color: var(--ink-3); }

      .cti-v3-script-progress {
        display: flex; align-items: center; gap: 4px; padding: 12px 16px 0;
      }
      .cti-v3-progress-seg {
        flex: 1; height: 4px; border: none; border-radius: 999px;
        background: var(--line); transition: background 200ms;
      }
      .cti-v3-progress-seg.done { background: var(--good); }
      .cti-v3-progress-seg.active { background: var(--call); }
      .cti-v3-progress-count { margin-left: 6px; font-size: 10px; font-weight: 500; color: var(--ink-3); }

      .cti-v3-script-body { padding: 16px; }
      .cti-v3-script-badge { margin-bottom: 8px; display: flex; align-items: center; gap: 8px; }
      .cti-v3-script-title { font-size: 12px; font-weight: 600; color: var(--ink); }
      .cti-v3-script-text { margin: 0; font-size: 15px; line-height: 1.6; color: var(--ink); }
      .cti-v3-script-hint {
        margin-top: 12px; padding: 8px 10px;
        border: 1px solid hsl(var(--warning) / 0.25); background: hsl(var(--warning) / 0.06); border-radius: 10px;
        display: flex; align-items: flex-start; gap: 8px; font-size: 11px; color: var(--ink-2); line-height: 1.4;
      }
      .cti-v3-script-checks { margin-top: 12px; display: flex; flex-direction: column; gap: 4px; }
      .cti-v3-script-check {
        display: flex; align-items: center; gap: 8px; padding: 6px 6px;
        border: none; background: transparent; border-radius: 8px; text-align: left; color: var(--ink);
      }
      .cti-v3-script-check:hover { background: var(--panel-2); }
      .cti-v3-check-box {
        display: grid; place-items: center; width: 16px; height: 16px; flex-shrink: 0;
        border: 1px solid var(--line-strong); border-radius: 5px; color: white; transition: all 150ms;
      }
      .cti-v3-check-box.on { border-color: var(--good); background: var(--good); }
      .cti-v3-check-label { font-size: 12px; color: var(--ink); }
      .cti-v3-check-label.done { color: var(--ink-3); text-decoration: line-through; }
      .cti-v3-script-branches { margin-top: 12px; }
      .cti-v3-branch-list { display: flex; flex-wrap: wrap; gap: 6px; }
      .cti-v3-branch-btn {
        display: flex; align-items: center; gap: 6px;
        padding: 6px 10px; border-radius: 8px;
        border: 1px solid var(--line); background: var(--panel-2);
        font-size: 11px; font-weight: 500; color: var(--ink); transition: all 120ms;
      }
      .cti-v3-branch-btn:hover { border-color: hsl(var(--primary) / 0.40); background: hsl(var(--primary) / 0.06); }
      .cti-v3-branch-next { font-size: 10px; color: var(--ink-3); }
      .cti-v3-script-foot {
        margin-top: auto; display: flex; align-items: center; gap: 8px;
        padding: 10px 16px; border-top: 1px solid var(--line);
      }
      .cti-v3-foot-btn {
        display: inline-flex; align-items: center; gap: 4px;
        padding: 6px 10px; border-radius: 8px; border: 1px solid var(--line);
        background: transparent; color: var(--ink-2); font-size: 11px; font-weight: 500;
      }
      .cti-v3-foot-btn:hover:not(:disabled) { color: var(--ink); }
      .cti-v3-foot-btn:disabled { opacity: 0.35; cursor: not-allowed; }
      .cti-v3-foot-btn.primary { background: var(--ink); color: var(--panel); border-color: transparent; font-weight: 600; }
      .cti-v3-foot-btn.primary:hover:not(:disabled) { opacity: 0.9; }
      .cti-v3-foot-hint { margin-left: auto; display: inline-flex; align-items: center; gap: 6px; font-size: 10px; color: var(--ink-3); }

      .cti-v3-obj-list > * + * { border-top: 1px solid var(--line); }
      .cti-v3-obj-row { padding: 12px 16px; }
      .cti-v3-obj-q { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; color: var(--ink); margin-bottom: 4px; }
      .cti-v3-obj-a { margin: 0; font-size: 12px; color: var(--ink-2); line-height: 1.5; }

      /* Disposition */
      .cti-v3-prev { padding: 10px 16px; border-bottom: 1px solid var(--line); background: var(--panel-2); }
      .cti-v3-prev-toggle {
        display: flex; align-items: center; gap: 6px; width: 100%;
        border: none; background: transparent;
        font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.1em; color: var(--ink-3);
      }
      .cti-v3-prev-caret { margin-left: auto; transition: transform 120ms; }
      .cti-v3-prev-caret.open { transform: rotate(90deg); }
      .cti-v3-prev-list { margin-top: 8px; display: flex; flex-direction: column; gap: 6px; }
      .cti-v3-prev-item {
        padding: 6px 10px; border: 1px solid var(--line); background: var(--panel); border-radius: 9px;
      }
      .cti-v3-prev-meta { font-size: 9px; color: var(--ink-3); margin-bottom: 2px; }
      .cti-v3-prev-text { font-size: 11px; color: var(--ink-2); line-height: 1.4; }

      .cti-v3-dispo-body { padding: 16px; }
      .cti-v3-dispo-chips { display: flex; flex-wrap: wrap; gap: 6px; }
      .cti-v3-dispo-chip {
        padding: 6px 10px; border-radius: 8px;
        border: 1px solid var(--line); background: transparent; color: var(--ink-2);
        font-size: 11px; font-weight: 500; transition: all 150ms;
      }
      .cti-v3-dispo-chip:hover { border-color: var(--line-strong); color: var(--ink); }
      .cti-v3-dispo-chip.on { border-color: transparent; font-weight: 500; }

      .cti-v3-dispo-cb {
        margin-top: 12px; display: flex; flex-wrap: wrap; gap: 8px; align-items: flex-end;
        padding: 8px 10px; border: 1px solid hsl(var(--primary) / 0.25); background: hsl(var(--primary) / 0.05); border-radius: 10px;
      }
      .cti-v3-time-input {
        padding: 4px 8px; border-radius: 6px; border: 1px solid var(--line); background: var(--panel);
        color: var(--ink); font-size: 12px;
      }
      .cti-v3-cb-hint { padding-bottom: 4px; font-size: 10px; color: var(--ink-2); }

      .cti-v3-dispo-notes { margin-top: 12px; }
      .cti-v3-dispo-notes textarea {
        width: 100%; resize: vertical; padding: 8px 10px;
        border: 1px solid var(--line); background: var(--panel-2); border-radius: 10px;
        color: var(--ink); font-family: inherit; font-size: 12px; line-height: 1.5; outline: none;
      }
      .cti-v3-dispo-notes textarea::placeholder { color: var(--ink-3); }
      .cti-v3-dispo-notes textarea:focus { border-color: var(--line-strong); }

      .cti-v3-dispo-actions { margin-top: 12px; display: flex; align-items: center; gap: 8px; }
      .cti-v3-hint-text { font-size: 10px; color: var(--ink-3); }

      /* History */
      .cti-v3-hist-list > * + * { border-top: 1px solid var(--line); }
      .cti-v3-hist-row { padding: 10px 14px; }
      .cti-v3-hist-line { display: flex; align-items: center; gap: 8px; }
      .cti-v3-hist-at { font-size: 10px; color: var(--ink-3); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .cti-v3-hist-dispo { margin-left: auto; font-size: 10px; font-weight: 600; }
      .cti-v3-hist-agent { margin-top: 4px; display: flex; align-items: center; gap: 8px; font-size: 11px; color: var(--ink-2); }
      .cti-v3-hist-dur { margin-left: auto; }
      .cti-v3-hist-note { margin: 4px 0 0; font-size: 11px; color: var(--ink-2); line-height: 1.4; }

      /* Contacts */
      .cti-v3-search-input {
        flex: 1; min-width: 0; border: none; outline: none; background: transparent;
        font-size: 12px; color: var(--ink);
      }
      .cti-v3-search-input::placeholder { color: var(--ink-3); }
      .cti-v3-contacts-list { max-height: 260px; overflow-y: auto; }
      .cti-v3-eyebrow {
        padding: 8px 14px 4px; font-size: 10px; font-weight: 600;
        text-transform: uppercase; letter-spacing: 0.1em; color: var(--ink-3);
      }
      .cti-v3-eyebrow-2 { padding-top: 10px; }
      .cti-v3-contact-row {
        display: flex; align-items: center; gap: 8px; width: 100%;
        padding: 6px 14px; border: none; background: transparent; text-align: left;
        color: var(--ink); transition: background 120ms;
      }
      .cti-v3-contact-row:not(.static):hover { background: var(--panel-2); }
      .cti-v3-contact-body { flex: 1; min-width: 0; }
      .cti-v3-contact-name { display: block; font-size: 11px; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .cti-v3-contact-sub { display: block; font-size: 9px; color: var(--ink-3); }
      .cti-v3-contact-tag { font-size: 9px; color: var(--ink-3); flex-shrink: 0; }
      .cti-v3-contact-dial { color: var(--call); opacity: 0; transition: opacity 120ms; }
      .cti-v3-contact-row:hover .cti-v3-contact-dial { opacity: 1; }

      /* Call details */
      .cti-v3-details-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 12px; padding: 14px; }
      .cti-v3-detail-key {
        font-size: 9px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.07em; color: var(--ink-3);
      }
      .cti-v3-detail-value {
        margin-top: 2px; font-size: 11px; font-weight: 500; color: var(--ink);
        overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      }
      .cti-v3-rec-dot {
        display: inline-block; width: 6px; height: 6px; border-radius: 50%;
        background: var(--crit); color: var(--crit);
      }

      /* Events */
      .cti-v3-events { max-height: 150px; overflow-y: auto; }
      .cti-v3-events > * + * { border-top: 1px solid var(--line); }
      .cti-v3-event-row { display: flex; gap: 8px; padding: 5px 14px; }
      .cti-v3-event-at { flex-shrink: 0; font-size: 9px; color: var(--ink-3); }
      .cti-v3-event-text { font-size: 10px; color: var(--ink-2); line-height: 1.4; }

      /* ============================================================= */
      /*  Dashboard (idle mode)                                         */
      /* ============================================================= */
      .cti-v3-dash { display: flex; flex-direction: column; gap: 16px; }

      .cti-v3-kpi-grid {
        display: grid; gap: 10px;
        grid-template-columns: repeat(6, 1fr);
      }
      @media (max-width: 1200px) { .cti-v3-kpi-grid { grid-template-columns: repeat(3, 1fr); } }
      @media (max-width: 700px) { .cti-v3-kpi-grid { grid-template-columns: 1fr 1fr; } }
      .cti-v3-kpi { padding: 12px; }
      .cti-v3-kpi-label {
        font-size: 10px; font-weight: 500; text-transform: uppercase; letter-spacing: 0.07em; color: var(--ink-3);
        overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
      }
      .cti-v3-kpi-value {
        margin-top: 6px; font-size: 24px; font-weight: 600; line-height: 1;
        letter-spacing: -0.03em; color: var(--ink);
      }
      .cti-v3-kpi-sub { margin-top: 6px; font-size: 10px; color: var(--ink-3); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

      .cti-v3-dash-grid-1 { display: grid; gap: 16px; grid-template-columns: 1.4fr 1fr; }
      .cti-v3-dash-grid-2 { display: grid; gap: 16px; grid-template-columns: 1fr 1fr; }
      @media (max-width: 1200px) {
        .cti-v3-dash-grid-1, .cti-v3-dash-grid-2 { grid-template-columns: 1fr; }
      }

      /* Chart */
      .cti-v3-chart-wrap { height: 210px; width: 100%; display: flex; flex-direction: column; }
      .cti-v3-chart { width: 100%; height: 100%; }
      .cti-v3-grid-line { stroke: var(--line); stroke-dasharray: 2 4; stroke-width: 1; }
      .cti-v3-axis { fill: var(--ink-3); font-size: 9px; font-family: inherit; }
      .cti-v3-bar-in { fill: var(--call); }
      .cti-v3-bar-in-soft { fill: var(--call); fill-opacity: 0.3; }
      .cti-v3-bar-out { fill: var(--good); }
      .cti-v3-bar-out-soft { fill: var(--good); fill-opacity: 0.3; }
      .cti-v3-legend { margin-top: 6px; display: flex; flex-wrap: wrap; gap: 12px; font-size: 10px; color: var(--ink-3); }
      .cti-v3-legend span { display: inline-flex; align-items: center; gap: 4px; }
      .cti-v3-legend-swatch { width: 8px; height: 8px; border-radius: 2px; }
      .cti-v3-legend-swatch.soft { opacity: 0.4; }

      /* Disposition mix */
      .cti-v3-dispo-list > * + * { margin-top: 12px; }
      .cti-v3-dispo-row-head { display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 4px; }
      .cti-v3-dispo-label { display: flex; align-items: center; gap: 8px; font-size: 12px; color: var(--ink-2); }
      .cti-v3-dispo-swatch { width: 8px; height: 8px; border-radius: 3px; }
      .cti-v3-dispo-count { font-size: 13px; font-weight: 600; }
      .cti-v3-dispo-track {
        height: 7px; overflow: hidden; border-radius: 999px;
        background: var(--panel-2); box-shadow: inset 0 0 0 1px var(--line);
      }
      .cti-v3-dispo-fill { height: 100%; border-radius: 999px; }

      /* Follow-ups / leads */
      .cti-v3-link { border: none; background: transparent; color: var(--call); font-size: 11px; font-weight: 500; }
      .cti-v3-link:hover { opacity: 0.8; }
      .cti-v3-fu-at { font-size: 11px; font-weight: 600; }
      .cti-v3-fu-name { display: block; font-size: 12px; font-weight: 500; }
      .cti-v3-fu-num { display: block; font-size: 10px; color: var(--ink-3); }
      .cti-v3-fu-reason { font-size: 11px; color: var(--ink-2); }
      .cti-v3-fu-stage { font-size: 11px; }

      /* Roster info */
      .cti-v3-roster { display: flex; flex-wrap: wrap; align-items: center; gap: 12px 24px; padding: 16px; }
      .cti-v3-roster-label {
        font-size: 10px; font-weight: 500; text-transform: uppercase;
        letter-spacing: 0.07em; color: var(--ink-3);
      }
      .cti-v3-roster-value { margin-top: 2px; font-size: 12px; font-weight: 500; }
      .cti-v3-roster-campaigns { margin-top: 4px; display: flex; gap: 6px; }
      .cti-v3-roster-break { margin-left: auto; display: flex; align-items: center; gap: 8px; font-size: 11px; color: var(--ink-2); }

      /* ============================================================= */
      /*  Contact picker modal                                          */
      /* ============================================================= */
      .cti-v3-modal-backdrop {
        position: fixed; inset: 0; display: flex; align-items: flex-start; justify-content: center;
        padding-top: 12vh; background: hsl(var(--foreground) / 0.35); backdrop-filter: blur(2px); z-index: 70;
      }
      .cti-v3-modal {
        position: relative; width: 100%; max-width: 480px; overflow: hidden;
        border: 1px solid var(--line); border-radius: 14px; background: var(--panel);
        box-shadow: var(--shadow-lg);
      }
      .cti-v3-modal-head {
        display: flex; align-items: center; gap: 10px;
        padding: 12px 14px; border-bottom: 1px solid var(--line);
      }
      .cti-v3-modal-body { flex: 1; min-width: 0; }
      .cti-v3-modal-title { font-size: 13px; font-weight: 600; }
      .cti-v3-modal-sub { font-size: 10px; color: var(--ink-3); }
      .cti-v3-modal-search {
        display: flex; align-items: center; gap: 8px;
        padding: 8px 14px; border-bottom: 1px solid var(--line);
      }
      .cti-v3-modal-search input {
        flex: 1; min-width: 0; border: none; outline: none; background: transparent;
        color: var(--ink); font-size: 13px;
      }
      .cti-v3-modal-search input::placeholder { color: var(--ink-3); }
      .cti-v3-modal-scroll { max-height: 46vh; overflow-y: auto; padding: 4px 0; }
      .cti-v3-modal-eyebrow {
        padding: 8px 14px 4px; font-size: 10px; font-weight: 600;
        text-transform: uppercase; letter-spacing: 0.11em; color: var(--ink-3);
      }
      .cti-v3-picker-row {
        display: flex; align-items: center; gap: 10px;
        padding: 6px 14px; transition: background 120ms;
      }
      .cti-v3-picker-row:hover { background: var(--panel-2); }
      .cti-v3-picker-body { flex: 1; min-width: 0; }
      .cti-v3-picker-name { display: block; font-size: 12px; font-weight: 500; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .cti-v3-picker-sub { display: block; font-size: 10px; color: var(--ink-3); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .cti-v3-picker-btns { display: flex; gap: 4px; }
      .cti-v3-picker-btn {
        padding: 4px 8px; border: none; border-radius: 6px;
        background: var(--panel-2); box-shadow: inset 0 0 0 1px var(--line);
        color: var(--ink-2); font-size: 10px; font-weight: 600;
      }
      .cti-v3-picker-btn:not(:disabled):hover { color: var(--ink); }
      .cti-v3-picker-btn:disabled { opacity: 0.4; cursor: not-allowed; }
      .cti-v3-picker-btn.primary { background: hsl(var(--primary) / 0.10); color: var(--call); box-shadow: none; }
      .cti-v3-picker-btn.primary:hover { opacity: 0.8; }
    `}} />
  );
}
